<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { clients, georgeDiscussContext, agentName as agentNameStore, stages, chatSeed, prospectingTileSeed, terms, stageLabel } from '../lib/stores';
  import { tick } from 'svelte';
  import MetaTab from '../lib/MetaTab.svelte';
  import { createSplitRail } from '../lib/splitRail.svelte';
  import {
    getClientContacts, getInternalContacts,
    getContacts, saveDraft, polishEmail,
    getPersonas, setActivePersona,
    createSequence, getConversions, toggleConversionStar,
    getConversionSettings, toggleConversionTeamMode,
    getOutreachIndex, getProspectingCredits,
    strafePreview, strafeCommit, strafeDiscard, getStrafeJob, listStrafes,
    bulkUnschedule,
    type Contact, type PersonasData, type ConversionEmail, type OutreachEntry,
    type StrafePreviewSample, type StrafeJob, type StrafeEmail,
  } from '../lib/api';
  import { renderSafeMarkdown, unwrapHardBreaks, repairLostApostrophes } from '../lib/markdown';
  import MultiSelect from '../lib/MultiSelect.svelte';
  import EmailBodyEditor from '../lib/EmailBodyEditor.svelte';

  let activeTab = $state<'generate' | 'history' | 'strafe' | 'meta' | 'george'>('generate');
  // The strategy chat is a rail beside every tab; collapsed (or under 1100px) it
  // becomes a tab of its own. Same shell as the client page — app.css `.split-shell`.
  let shellEl = $state<HTMLElement>();
  const rail = createSplitRail({
    keyW: 'prRailW', keyCollapsed: 'prRailCollapsed', getShell: () => shellEl,
  });
  // When the rail is visible again, don't leave the (now-hidden) assistant tab active.
  $effect(() => { if (!rail.showTab && activeTab === 'george') activeTab = 'generate'; });
  /** Turn the conversation into a draft (Pipeline B). Lives on the chip row under
   *  the log, not on each assistant message — one act, one control. */
  async function draftFromChat() {
      // Routes through createSequence → generate_outreach
      // (Pipeline B) instead of the old chatApi text path.
      // Pipeline B gives us recommended_attachments, the
      // outreach catalog in the system prompt, banned
      // cliches / CTAs, per-tenant cta_rule, conversion
      // examples — all the things the chatApi text path
      // couldn't provide. The conversation goes in as the
      // `brief` so the chosen contact / angle from the
      // discussion shape the output.
      //
      // Preselect step: if the form has no client/contact
      // picked but the chat has at least one email mention,
      // call the preselect endpoint to pick the strongest
      // target from the conversation and fill the form
      // before drafting. Lets the chat-first flow draft
      // without the user dropping into the form.
      if (!selectedClient || !contactEmail) {
        const hasEmail = /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(
          chatMessages.map(m => m.content || '').join(' ')
        );
        if (!hasEmail) {
          showError('Pick a target in the form above first');
          return;
        }
        chatLoading = true;
        try {
          // Keep system messages — they hold the
          // "📋 Looked up: <Client>" trail from George's
          // read_client tool calls, and the preselect
          // backend matches those slugs against CRM to
          // pull contacts for clients George researched
          // even when the user never typed their name.
          const pick = await (await import('../lib/api')).preselectTarget(
            chatMessages
              .filter(m => !m.hidden)
              .map(m => ({ role: m.role, content: m.content })),
          );
          // Suppress onClientChange's contactEmail wipe
          // while we apply the picked target — same pattern
          // the prospectingTileSeed flow uses. Without
          // _seedActive, assigning selectedClient triggers
          // onClientChange() which sets contactEmail=''
          // BEFORE the subsequent contactEmail = pick.email
          // can take effect in the rendered TO input.
          _seedActive = true;
          selectedClient = pick.company || selectedClient;
          contactEmail   = pick.email   || contactEmail;
          contactName    = pick.person_name || contactName;
          await tick();
          _seedActive = false;
          chatMessages = [...chatMessages,
            { role: 'system', content: `🎯 Target: ${pick.person_name} <${pick.email}> — ${pick.rationale}` },
          ];
        } catch (err: any) {
          chatLoading = false;
          showError('Could not auto-pick a target: ' + (err?.message || ''));
          return;
        }
      } else {
        chatLoading = true;
      }
      try {
        // Last few chat exchanges as the brief — captures
        // the angle / contact George has been recommending.
        // Cap each turn so a long monologue doesn't blow
        // the prompt; cap on 3 round-trips so older context
        // doesn't drown the most recent decision.
        const briefTurns = chatMessages
          .filter(m => m.role !== 'system' && !m.hidden)
          .slice(-6);
        const briefText = briefTurns
          .map(m => `${m.role.toUpperCase()}: ${m.content.slice(0, 1500)}`)
          .join('\n\n');
        const brief = `Based on the conversation below, draft a single cold outreach email that matches the angle, contact, and approach the discussion has converged on. Keep it concise.\n\n${briefText}`;
        const seq = await createSequence({
          client: selectedClient,
          contact_email: contactEmail,
          contact_name: contactName || undefined,
          brief,
          persona: selectedPersona || undefined,
          voice: 'user',
          num_emails: 1,
          delay_2: 3, delay_3: 7,
        });
        researchContext = seq.research || '';
        // The chat is a rail beside EVERY tab, so a draft raised from here
        // can land on a tab that does not show it. Go to the one that does —
        // otherwise the button reads as having done nothing.
        activeTab = 'generate';
        generatedVersion++;
        generatedEmails = (seq.emails || []).map((e: any) => ({
          step: e.step,
          subject: e.subject,
          body: e.body,
          recommended_attachments: e.recommended_attachments || [],
          editTo: contactEmail,
          editSubject: e.subject,
          editBody: e.body || '',
          editCcClient: [...editCcClient],
          editCcInternal: [...editCcInternal],
          editCcOther: editCcOther,
          polishing: false,
          saving: false,
          polishChanges: '',
          selectedDocs: (e.recommended_attachments || []).filter(
            (id: string) => marketingDocs.some(d => d.id === id),
          ),
          customFiles: [] as File[],
        }));
        if (seq.emails?.length) {
          const first = seq.emails[0];
          draft = {
            to: contactEmail, subject: first.subject, body: first.body,
            recommended_attachments: first.recommended_attachments || [],
            research_summary: '',
          };
        }
        // createSequence debits a prospecting credit —
        // refresh the counter so the UI stays in sync.
        getProspectingCredits().then(c => {
          creditsFree = c.free;
          creditsRemaining = c.remaining; creditsLoaded = true;
          creditsCostPer = c.cost_per ?? creditsCostPer;
        }).catch(() => {});
      } catch (err: any) {
        showError('Failed to draft email: ' + (err?.message || ''));
      }
      chatLoading = false;
  }

  /** Anything that PUTS a message in the chat must also make the chat visible —
   *  otherwise a seeded conversation lands somewhere the user cannot see. */
  function revealChat() {
    rail.reveal();
    if (rail.showTab) activeTab = 'george';
  }
  const isDealton = $derived($userEmail.toLowerCase().endsWith('@dealton.ai'));
  let conversions = $state<ConversionEmail[]>([]);

  // Sorting mirrors the Dashboard table: click a header to sort, click again to flip,
  // text columns default ascending and numeric ones descending. It sorts WITHIN each
  // group — which rows are in use is the selection rule, not a sort preference.
  // Three orderings earn a header: `score` IS the rule (and the way back to the view
  // where the divider means anything), `replied` answers "what worked recently", and
  // `owner` groups a teammate's emails together. Client and Subject are read, not
  // sorted — an arrow on every column implies orders nobody asks for.
  type ConvSort = 'score' | 'replied' | 'owner';
  // Counted from the server's own `learned` flag, never from a local constant: the cap
  // lives in `state_db.MAX_COPIED`, and a second copy here is how "5" drifts. Counted
  // over ALL rows, not the visible ones, so narrowing the view cannot make the label lie.
  const learnedCount = $derived(conversions.filter(c => c.learned).length);
  let usedOpen = $state(true);
  let restOpen = $state(true);

  let convSort = $state<ConvSort>('score');
  let convSortDir = $state<'asc' | 'desc'>('desc');
  function convSortBy(k: ConvSort) {
    if (convSort === k) convSortDir = convSortDir === 'asc' ? 'desc' : 'asc';
    else { convSort = k; convSortDir = 'desc'; }   // both are 'best/most recent first'
  }
  const convSortInd = (k: ConvSort) => (convSort !== k ? '▼' : convSortDir === 'asc' ? '▲' : '▼');
  const convKey = (c: ConversionEmail): string | number =>
    convSort === 'replied' ? (c.inbound_date || '')
    : convSort === 'owner' ? c.user_email.split('@')[0].toLowerCase()
    : (c.score || 0);

  /** "same day" / "+3d" between the outbound and the reply. Reply speed is the part of a
   *  conversion that says how strong the example is; the two raw dates did not. */
  function replyLatency(out?: string, inb?: string): string {
    if (!out || !inb) return '';
    const d = Math.round((Date.parse(inb) - Date.parse(out)) / 86400000);
    if (!Number.isFinite(d) || d < 0) return '';
    return d === 0 ? 'same day' : `+${d}d`;
  }
  let creditsFree = $state(20);
  let creditsRemaining = $state(20);
  // The 20s above are placeholders, never a real balance. Every fetch swallows its
  // error, so an unguarded badge asserts "20/20" for a tenant it never loaded.
  let creditsLoaded = $state(false);
  // Server-owned, never a frontend constant — it is a billing figure.
  let creditsCostPer = $state(0.2);
  let expandedId = $state('');
  let teamMode = $state(false);
  // The generator's rule, restated as two numbers: pins first (org-wide, `team_mode` does
  // not apply to them), then the highest-scoring rows from the pool the toggle scopes.
  // `autoCount` IS how many slots the toggle governs — at 0 it governs nothing, which is
  // why the control disables itself rather than sitting there looking live.
  const pinnedCount = $derived(conversions.filter(c => c.learned && c.selected).length);
  const autoCount = $derived(learnedCount - pinnedCount);
  const slotSplit = $derived(
    autoCount === 0 ? `all ${pinnedCount} pinned — no slot left to fill`
    : pinnedCount === 0 ? `${autoCount} top-scoring, ${teamMode ? "team-wide" : "from my emails"}`
    : `${pinnedCount} pinned · ${autoCount} top-scoring, ${teamMode ? "team-wide" : "from my emails"}`);
  const segTitle = $derived(
    autoCount === 0
      ? `All ${learnedCount} slots are pinned, so this setting has nothing left to choose. Unpin one to auto-fill it.`
      : `Where the ${autoCount === 1 ? 'remaining slot is' : `remaining ${autoCount} slots are`} filled from. Pinned emails are used either way.`);
  let marketingDocs = $state<OutreachEntry[]>([]);
  let toast = $state('');
  let toastError = $state(false);

  // Prospecting chat — `hidden` lets a chat-seed inject a user prompt that
  // never renders; `isSeedLabel` flags the small italic header above it.
  let chatMessages = $state<{ role: string; content: string; hidden?: boolean; isSeedLabel?: boolean }[]>([]);
  let chatInput = $state('');
  let chatLoading = $state(false);
  /** Messages typed while a request is in flight; drained by `processChat`. */
  let chatQueue = $state<string[]>([]);
  // "Find lookalike prospects" button state — disabled while discovery
  // is in flight (Brave + Claude + Hunter/ZB stack, ~30-90s).
  let discoveringLookalikes = $state(false);
  // Temporarily hidden — flip to true to re-show the "Find new prospects"
  // (lead discovery) button. Functionality is untouched.
  const SHOW_FIND_PROSPECTS = false;

  let chatMode = $state<'cro' | 'george' | 'user'>((localStorage.getItem('chatMode') as any) || 'cro');
  $effect(() => { localStorage.setItem('chatMode', chatMode); });

  import { chat as chatApi, discoverLookalikes } from '../lib/api';

  // The dev chatMode toggle is one of `voiceMode`'s inputs. `cro` clears the override
  // rather than leaving the last one in place — that omission is what made the old
  // version order-dependent.
  $effect(() => {
    voiceOverride = chatMode === 'george' ? 'persona' : chatMode === 'user' ? 'user' : null;
  });

  async function processChat() {
    chatLoading = true;
    try {
      const allMessages = chatMessages.map(m => ({ role: m.role, content: m.content }));
      const r = await chatApi({ client: '', messages: allMessages, ref_thread: '', mode: chatMode });
      // Show tool calls as a system message if any
      if (r.tool_calls?.length) {
        const toolSummary = r.tool_calls.map((t: any) => {
          if (t.tool === 'web_search') return `🔍 Searched: "${t.input}"`;
          if (t.tool === 'read_client') return `📋 Looked up: ${t.input}`;
          if (t.tool === 'read_threads') return `📧 Listed threads: ${t.input}`;
          if (t.tool === 'read_email') return `✉️ Read email: ${t.input}`;
          if (t.tool === 'read_knowledge') return `📚 Read: ${t.input}`;
          if (t.tool === 'read_calls') return `📞 Call history: ${t.input}`;
          if (t.tool === 'read_memory') return `🧠 Read memory: ${t.input}`;
          return `🔧 ${t.tool}: ${t.input}`;
        }).join('\n');
        chatMessages = [...chatMessages, { role: 'system', content: toolSummary }];
      }
      chatMessages = [...chatMessages, { role: 'assistant', content: r.reply }];
    } catch (err: any) {
      // Surface the reason. A bare `catch` here discarded it, so a Gmail rate-limit (429), an
      // Anthropic overload (503, mapped from 529 in `main.py:240`), an expired session and a
      // paywall all rendered as one indistinguishable "something went wrong" — while
      // `main.py:232-242` goes to deliberate trouble to tell them apart. Matches ClientView's
      // rail and the five other catches in this file, which all already do this.
      chatMessages = [...chatMessages, {
        role: 'assistant',
        content: 'Error: ' + (err?.message || 'Request failed'),
      }];
    }
    chatLoading = false;

    // Drain anything typed while the request was in flight.
    if (chatQueue.length > 0) {
      chatQueue = [];
      await processChat();
    }
  }

  /** Fire a suggested question as if the user had typed it. */
  function quickAsk(q: string) {
    if (chatLoading) return;
    chatInput = q;
    sendChat();
  }

  async function sendChat() {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    chatInput = '';
    chatMessages = [...chatMessages, { role: 'user', content: userMsg }];
    // Sending mid-request used to `return` before the message was ever added: the turn was
    // silently dropped, with the text left in the box and nothing to say why. Queue it, as
    // ClientView's rail does — the user's message appears immediately either way.
    if (chatLoading) {
      chatQueue = [...chatQueue, userMsg];
      return;
    }
    await processChat();
  }

  // Trigger the same lookalike pipeline that powers the dry_funnel brief
  // — without the "funnel is dry" framing. Result is seeded into the
  // chat as a structured assistant message so the user can immediately
  // talk through which company to act on.
  async function findLookalikes() {
    if (discoveringLookalikes) return;
    discoveringLookalikes = true;
    revealChat();
    const placeholder = { role: 'system', content: 'Researching lookalike prospects — this takes 30-90s (Brave + Claude + email verification)…', hidden: false } as any;
    chatMessages = [...chatMessages, placeholder];
    try {
      const brief = await discoverLookalikes();
      // Drop the placeholder, append the result as an assistant message.
      const intro = `Here are ${brief.prospects.length} lookalike prospects I found across ${Object.keys(brief.domain_patterns).length} companies, based on your top accounts and the buyer-roles you already engage. Want to pick one to start with?`;
      chatMessages = chatMessages.filter(m => m !== placeholder);
      chatMessages = [...chatMessages,
        { role: 'assistant', content: intro },
        { role: 'assistant', content: brief.markdown },
      ];
    } catch (err: any) {
      chatMessages = chatMessages.filter(m => m !== placeholder);
      chatMessages = [...chatMessages, { role: 'assistant', content: `Couldn't find lookalikes: ${err?.message || 'unknown error'}` }];
    } finally {
      discoveringLookalikes = false;
    }
  }

  // Pick up George discuss context from Dashboard
  $effect(() => {
    if ($georgeDiscussContext) {
      chatMessages = [{ role: 'assistant', content: $georgeDiscussContext }];
      revealChat();
      $georgeDiscussContext = '';
    }
  });

  // Chat seed (e.g. the dry_funnel briefing) — inject a hidden problem-statement
  // user prompt and auto-generate the response. The italic seed label shows
  // what the user clicked; the raw prompt is never rendered.
  $effect(() => {
    const seed = $chatSeed;
    if (seed) {
      $chatSeed = null;
      tick().then(async () => {
        revealChat();
        chatMessages = [
          { role: 'system', content: seed.label, isSeedLabel: true },
          { role: 'user', content: seed.prompt, hidden: true },
        ];
        await processChat();
      });
    }
  });

  // Prospecting tile seed — set when the user clicks Discuss on a Today
  // prospecting tile. Pre-fills the Generate tab with the target, the
  // contact, and the existing draft, so the user lands on a ready-to-
  // edit/regenerate state instead of an empty form.
  $effect(() => {
    const seed = $prospectingTileSeed;
    if (!seed) return;
    $prospectingTileSeed = null;
    tick().then(async () => {
      _seedActive = true;
      try {
        activeTab = 'generate';
        numEmails = 1;            // single email — original prospecting draft is one
        voiceOverride = 'user';   // the tile's draft is in the rep's own voice
        instruction = seed.brief || '';
        contactName = seed.contactName || '';
        // Assigning selectedClient triggers the reactive effect at the
        // bottom of this script → onClientChange(). _seedActive=true
        // makes that call non-destructive (no contactEmail blank, no
        // clientEmails-driven auto-pick, no generatedEmails wipe).
        selectedClient = seed.client;
        contactEmail = seed.contactEmail || '';
        // Seed the editable card with the existing draft so the user
        // can edit / polish / regenerate without re-running generation
        // from a blank slate.
        generatedVersion++;
        generatedEmails = [{
          step: 1,
          subject: seed.subject || '',
          body: seed.body || '',
          recommended_attachments: [],
          editTo: seed.contactEmail || '',
          editSubject: seed.subject || '',
          editBody: seed.body || '',
          editCcClient: [],
          editCcInternal: [],
          editCcOther: '',
          polishing: false,
          saving: false,
          polishChanges: '',
          selectedDocs: [],
          customFiles: [] as File[],
        }];
        draft = {
          to: seed.contactEmail || '',
          subject: seed.subject || '',
          body: seed.body || '',
          recommended_attachments: [],
          research_summary: '',
        };
      } finally {
        // Hold the flag through one more tick so a late-resolving
        // getClientContacts inside onClientChange can't auto-pick a
        // contactEmail and clobber the seeded one.
        await tick();
        _seedActive = false;
      }
    });
  });

  // Generate tab state
  let selectedClient = $state('');
  let contactEmail = $state('');
  let contactName = $state('');
  let instruction = $state('');
  let generating = $state(false);
  let numEmails = $state(3);  // 1 or 3
  let researchContext = $state('');
  /** Which voice `createSequence` is asked for. ONE owner: this derivation.
   *  It used to be plain state written from three places — an effect keyed off
   *  `chatMode`, the persona `<select>`'s onchange, and the tile seed — none aware of
   *  the others, so the value that reached the API depended on which control was
   *  touched last. Those three are now INPUTS, and `voiceOverride` is the only way to
   *  say "ignore the persona": explicit, clearable, and with a defined value for every
   *  `chatMode` (the old effect handled two of three and let `cro` keep whatever was
   *  there, which is what made it sticky). */
  let voiceOverride = $state<'user' | 'persona' | null>(null);
  let voiceMode = $derived.by(() => {
    if (voiceOverride) return voiceOverride;
    const p = personasData?.personas[selectedPersona];
    return (p?.special === 'emulate' || selectedPersona === userPersonaKey) ? 'user' : 'persona';
  });

  // Sequence timing
  let delay2Days = $state(3);
  let delay3Days = $state(7);
  let sendingSequence = $state(false);

  // Schedule send (single email)
  let scheduleOpen = $state(false);
  let scheduleDate = $state('');
  let scheduleTime = $state('');

  function openPicker(e: Event) {
    const el = e.currentTarget as HTMLInputElement;
    try { el.showPicker?.(); } catch {}
  }

  import { userEmail, colorScheme, scoreBadgeStyle } from '../lib/stores';
  let userPersonaKey = $derived($userEmail ? $userEmail.split('@')[0].split('.')[0].toLowerCase() : '');
  let generatedEmails = $state<Array<{
    step: number; subject: string; body: string;
    recommended_attachments: string[];
    editTo: string; editSubject: string; editBody: string;
    editCcClient: string[]; editCcInternal: string[]; editCcOther: string;
    // Per-card UI state. Both builders (the seed path and the sequence map)
    // always populate these, and the Send/Polish/Save handlers iterate them
    // directly — so they're required, not optional (optional forced spurious
    // 'possibly undefined' at every iteration site).
    polishing: boolean; saving: boolean; polishChanges: string;
    selectedDocs: string[]; customFiles: File[];
  }>>([]);
  // Bumped every time generatedEmails is replaced (by Generate, the chat
  // "Draft email", or the prospectingTileSeed) so each card's {#each}
  // key changes → forces remount. Without this, an EmailBodyEditor that
  // was mounted with an empty initial value keeps its empty `inner`
  // $state when generatedEmails is reassigned — same nested-bind reactivity
  // bug Scheduled.svelte hit with `bind:value={selected.body}`.
  let generatedVersion = $state(0);
  let draft = $state<{
    to: string; subject: string; body: string;
    recommended_attachments: string[]; research_summary: string;
  } | null>(null);

  // Edit draft state
  let editCcClient = $state<string[]>([]);
  let editCcInternal = $state<string[]>([]);
  let editCcOther = $state('');

  // Client contacts + master contacts for name lookup
  let clientEmails = $state<string[]>([]);
  let internalEmails = $state<string[]>([]);
  let allContacts = $state<Contact[]>([]);

  // Personas
  let personasData = $state<PersonasData | null>(null);
  let selectedPersona = $state('');


  // ── Strafe tab state ─────────────────────────────────────────────────
  type EngagementBucket = 'never_contacted' | 'engaged' | 'stale';
  const ENGAGEMENT_LABELS: Record<EngagementBucket, string> = {
    never_contacted: 'New',
    engaged: 'Recent',
    stale: 'Stale',
  };

  let strafeContactsLoaded = $state(false);
  let strafeAllContacts = $state<Contact[]>([]);
  let strafeFilterTags = $state<Set<string>>(new Set());      // selected tag chips ('' represents Untagged)
  let strafeFilterStages = $state<Set<string>>(new Set());
  let strafeFilterEngagement = $state<Set<EngagementBucket>>(new Set());
  let strafeUncheckedEmails = $state<Set<string>>(new Set()); // explicit unchecks within current filter

  let strafeStrategy = $state('');
  let strafePersona = $state('');
  let strafeNumEmails = $state<1 | 3>(3);
  let strafeThreadChain = $state(false);
  let strafeUseConversions = $state(true);
  let strafeDispatchStagger = $state(60);
  let strafeInitialOffsetMinutes = $state(0);  // matches Meta's units
  let strafeDelay2Days = $state(3);
  let strafeDelay3Days = $state(7);

  let strafePreviewing = $state(false);
  let strafePreviewJobId = $state('');
  let strafePreviewSamples = $state<StrafePreviewSample[]>([]);
  let strafePreviewExpanded = $state<Set<string>>(new Set());
  let strafePreviewTotalPlanned = $state(0);
  let strafeCommitting = $state(false);
  let strafeTargetsOpen = $state(true);
  /** The recipient list the on-screen preview was generated for. The live list is
   *  derived from the filter chips, which stay editable WHILE a preview is shown, and
   *  `resumeStrafePreview` restores a job's settings without its targets — so without
   *  this snapshot the samples can quietly stop describing the send that commit fires. */
  let strafePreviewedFor = $state<string[]>([]);

  let strafeJobs = $state<StrafeJob[]>([]);
  let strafeJobPolls = $state<Record<string, ReturnType<typeof setInterval>>>({});

  function classifyEngagement(c: Contact): EngagementBucket {
    if (!c.thread_count) return 'never_contacted';
    if (!c.last_seen) return 'stale';
    const ls = new Date(c.last_seen);
    if (isNaN(ls.getTime())) return 'stale';
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return ls.getTime() >= cutoff ? 'engaged' : 'stale';
  }

  let strafeAllTags = $derived.by(() => {
    const s = new Set<string>();
    for (const c of strafeAllContacts) s.add(c.tag || '');
    return Array.from(s).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
  });

  let strafeAllStages = $derived.by(() => {
    const s = new Set<string>();
    for (const c of strafeAllContacts) {
      const stage = $stages[c.client] || '';
      if (stage && stage !== 'Non-Clients') s.add(stage);
    }
    return Array.from(s).sort();
  });

  let strafeFilteredContacts = $derived.by(() => {
    return strafeAllContacts.filter(c => {
      // Strafe never targets Non-Clients — they're explicitly excluded from prospecting outreach.
      if (($stages[c.client] || '') === 'Non-Clients') return false;
      if (strafeFilterTags.size && !strafeFilterTags.has(c.tag || '')) return false;
      if (strafeFilterStages.size) {
        const stage = $stages[c.client] || '';
        if (!strafeFilterStages.has(stage)) return false;
      }
      if (strafeFilterEngagement.size && !strafeFilterEngagement.has(classifyEngagement(c))) return false;
      return true;
    });
  });

  let strafeSelectedEmails = $derived.by(() => {
    return strafeFilteredContacts
      .filter(c => !strafeUncheckedEmails.has(c.email))
      .map(c => c.email);
  });

  /** The preview no longer describes the send. Commit stays enabled — the operator may
   *  have deliberately trimmed one name — but it says so, loudly and in the confirm. */
  let strafePreviewStale = $derived.by(() => {
    if (strafePreviewSamples.length === 0) return false;
    if (strafeSelectedEmails.length !== strafePreviewedFor.length) return true;
    const previewed = new Set(strafePreviewedFor);
    return strafeSelectedEmails.some(e => !previewed.has(e));
  });
  /** What still stands between the operator and a preview, named by step. */
  let strafeBlockers = $derived.by(() => {
    const out: string[] = [];
    if (strafeSelectedEmails.length < 1) out.push('Step 2: check at least one target');
    if (!strafeStrategy.trim()) out.push('Step 3: write a strategy');
    return out;
  });
  let strafeCost = $derived(strafeSelectedEmails.length * (strafeNumEmails as number));
  /** Credits this send would take beyond what is free. `creditsRemaining` can be
   *  negative once the allowance is spent, hence the floor. */
  let strafeOverage = $derived(Math.max(0, strafeCost - Math.max(0, creditsRemaining)));

  /** An uncheck must not outlive the filter it was made under. `strafeUncheckedEmails`
   *  is a bare email set, so without this a contact unchecked under one filter comes
   *  back unchecked under another with nothing on screen saying so — and
   *  `strafeUncheckAll()` freezes an entire filtered set into it, which then silently
   *  drops those addresses from every later filter that happens to include them.
   *  Both directions send the wrong list. Pruning keeps the state visible: leave a
   *  filter and the exclusion is forgotten, so returning shows the box checked. */
  $effect(() => {
    const inFilter = new Set(strafeFilteredContacts.map(c => c.email));
    let stale = false;
    for (const e of strafeUncheckedEmails) if (!inFilter.has(e)) { stale = true; break; }
    if (!stale) return;
    const next = new Set<string>();
    for (const e of strafeUncheckedEmails) if (inFilter.has(e)) next.add(e);
    strafeUncheckedEmails = next;
  });

  function strafeToggleTag(t: string) {
    const next = new Set(strafeFilterTags);
    next.has(t) ? next.delete(t) : next.add(t);
    strafeFilterTags = next;
  }
  function strafeToggleStage(s: string) {
    const next = new Set(strafeFilterStages);
    next.has(s) ? next.delete(s) : next.add(s);
    strafeFilterStages = next;
  }
  function strafeToggleEngagement(b: EngagementBucket) {
    const next = new Set(strafeFilterEngagement);
    next.has(b) ? next.delete(b) : next.add(b);
    strafeFilterEngagement = next;
  }
  function strafeToggleContact(email: string) {
    const next = new Set(strafeUncheckedEmails);
    next.has(email) ? next.delete(email) : next.add(email);
    strafeUncheckedEmails = next;
  }
  function strafeUncheckAll() {
    strafeUncheckedEmails = new Set(strafeFilteredContacts.map(c => c.email));
  }
  function strafeCheckAll() {
    strafeUncheckedEmails = new Set();
  }
  function strafeInvertSelection() {
    const cur = new Set(strafeUncheckedEmails);
    const next = new Set<string>();
    for (const c of strafeFilteredContacts) {
      if (!cur.has(c.email)) next.add(c.email);
    }
    strafeUncheckedEmails = next;
  }

  async function loadStrafeData() {
    if (strafeContactsLoaded) return;
    strafeAllContacts = await getContacts();
    strafeContactsLoaded = true;
    try {
      strafeJobs = await listStrafes();
    } catch {}
    // Seed persona from active persona used in Outreach tab
    if (!strafePersona) strafePersona = selectedPersona;
  }

  async function runStrafePreview() {
    if (strafePreviewing) return;
    if (!strafeStrategy.trim()) { showToast('Strategy is required'); return; }
    if (strafeSelectedEmails.length < 1) { showToast('Select at least 1 prospect to preview'); return; }
    strafePreviewing = true;
    strafePreviewSamples = [];
    strafePreviewExpanded = new Set();
    try {
      const res = await strafePreview({
        contact_emails: strafeSelectedEmails,
        strategy: strafeStrategy,
        persona: strafePersona,
        num_emails: strafeNumEmails,
        initial_offset_minutes: Math.max(0, Math.floor(strafeInitialOffsetMinutes)),
        delay_2_days: strafeDelay2Days,
        delay_3_days: strafeDelay3Days,
        thread_chain: strafeNumEmails === 3 && strafeThreadChain,
        dispatch_stagger_seconds: Math.max(0, Math.floor(strafeDispatchStagger)),
        use_conversion_examples: strafeUseConversions,
      });
      strafePreviewJobId = res.job_id;
      strafePreviewedFor = [...strafeSelectedEmails];
      strafePreviewSamples = res.samples;
      strafePreviewTotalPlanned = res.total_planned;
      strafePreviewExpanded = new Set(res.samples.map(s => s.contact_email));
      try { const c = await getProspectingCredits(); creditsFree = c.free; creditsRemaining = c.remaining; creditsLoaded = true; } catch {}
    } catch (err: any) {
      showError(err?.message || 'Preview failed');
    }
    strafePreviewing = false;
  }

  async function commitStrafe() {
    if (!strafePreviewJobId || strafeCommitting) return;
    // Sending one email asks `confirm()` first; this sends dozens, to real prospects,
    // with no undo — so it asks too, and the question carries the numbers rather than
    // a bare "are you sure". Everything named here is otherwise only visible in small
    // print further up the page, or (for the stale case) not visible at all.
    const people = strafeSelectedEmails.length;
    const ask = [
      `Send ${strafeCost} email${strafeCost === 1 ? '' : 's'} to ${people} prospect${people === 1 ? '' : 's'}?`,
      '',
      `${strafeNumEmails} per prospect · first goes out in ${Math.max(0, Math.floor(strafeInitialOffsetMinutes))} min · ${Math.max(0, Math.floor(strafeDispatchStagger))}s between recipients.`,
    ];
    if (strafeOverage > 0) {
      ask.push(`${strafeCost} credits — ${strafeOverage} past your free balance, about $${(strafeOverage * creditsCostPer).toFixed(2)}.`);
    }
    if (strafePreviewStale) {
      ask.push('', `The recipient list changed since you previewed — previewed ${strafePreviewedFor.length}, sending to ${people}. The samples above do not describe this send.`);
    }
    ask.push('', 'This sends real email and cannot be undone.');
    if (!confirm(ask.join('\n'))) return;
    strafeCommitting = true;
    try {
      const res = await strafeCommit(strafePreviewJobId, strafeSelectedEmails);
      showToast(`Strafe queued: ${res.scheduled_now} scheduled now, ${res.queued} generating in background`);
      strafePreviewJobId = '';
      strafePreviewSamples = [];
      try { strafeJobs = await listStrafes(); } catch {}
      const jobId = res.job_id;
      pollStrafeJob(jobId);
    } catch (err: any) {
      showError(err?.message || 'Commit failed');
    }
    strafeCommitting = false;
  }

  async function discardStrafe() {
    if (!strafePreviewJobId) return;
    try {
      await strafeDiscard(strafePreviewJobId);
    } catch {}
    strafePreviewJobId = '';
    strafePreviewSamples = [];
    strafePreviewExpanded = new Set();
  }

  /** Poll one running job until it settles.
   *  ⚠️ Every interval started here MUST be reachable from `strafeJobPolls`, because
   *  `onDestroy` below is what stops them. Without that teardown the poll outlives the
   *  component and keeps writing into dead state for the life of the tab — and since
   *  the `strafeJobPolls` guard is component state, remounting the page would start a
   *  SECOND interval for the same job on top of the orphan. */
  const POLL_MS = 3000;
  const POLL_MAX_FAILURES = 5;
  function pollStrafeJob(jobId: string) {
    if (strafeJobPolls[jobId]) return;
    let failures = 0;
    const stop = () => { clearInterval(handle); delete strafeJobPolls[jobId]; };
    const handle = setInterval(async () => {
      try {
        const r = await getStrafeJob(jobId);
        failures = 0;
        const idx = strafeJobs.findIndex(j => j.id === jobId);
        if (idx >= 0) strafeJobs[idx] = r.job;
        if (r.job.status === 'done' || r.job.status === 'failed') stop();
      } catch {
        // A swallowed error here used to mean a dead endpoint or an expired session
        // polled forever in silence. Give up after a few in a row and say so once —
        // the job is still on the server, it is only this page's view that stopped.
        if (++failures >= POLL_MAX_FAILURES) {
          stop();
          showError('Lost contact with the strafe job — reload to resume tracking it.');
        }
      }
    }, POLL_MS);
    strafeJobPolls[jobId] = handle;
  }

  onDestroy(() => {
    for (const handle of Object.values(strafeJobPolls)) clearInterval(handle);
    strafeJobPolls = {};
  });

  async function unscheduleStrafeJob(jobId: string) {
    if (!confirm('Cancel all pending emails for this strafe?')) return;
    try {
      const r = await bulkUnschedule({ strafe_job_id: jobId });
      showToast(`${r.cancelled} email${r.cancelled === 1 ? '' : 's'} cancelled`);
    } catch (err: any) {
      showError(err?.message || 'Bulk cancel failed');
    }
  }

  function strafePreviewToggle(email: string) {
    const next = new Set(strafePreviewExpanded);
    next.has(email) ? next.delete(email) : next.add(email);
    strafePreviewExpanded = next;
  }

  async function resumeStrafePreview(jobId: string) {
    try {
      const r = await getStrafeJob(jobId);
      if (r.job.status !== 'preview') {
        showToast(`Job is in '${r.job.status}' state and can't be resumed`);
        return;
      }
      strafeStrategy = r.job.strategy;
      strafePersona = r.job.persona || '';
      strafeNumEmails = (r.job.num_emails === 1 ? 1 : 3);
      strafeInitialOffsetMinutes = r.job.initial_offset_minutes ?? 0;
      strafeDelay2Days = r.job.delay_2_days ?? 3;
      strafeDelay3Days = r.job.delay_3_days ?? 7;
      strafeThreadChain = !!r.job.thread_chain;
      strafeDispatchStagger = r.job.dispatch_stagger_seconds ?? 60;
      strafeUseConversions = r.job.use_conversion_examples !== false;
      strafePreviewJobId = jobId;
      // The job's OWN targets, not the current filter — resuming restores settings but
      // the live recipient list still comes from the chips, so these normally differ
      // and the staleness banner is right to fire.
      strafePreviewedFor = r.targets.map(t => t.contact_email);
      strafePreviewTotalPlanned = r.job.total;
      strafePreviewSamples = r.targets
        .filter(t => t.is_sample && t.status === 'previewed')
        .map(t => ({
          contact_email: t.contact_email,
          contact_name: t.contact_name,
          client: t.client,
          engagement: t.engagement ?? 'never_contacted',
          emails: (t.emails ?? []) as StrafeEmail[],
        }));
      strafePreviewExpanded = new Set(strafePreviewSamples.map(s => s.contact_email));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      showError(err?.message || 'Failed to resume preview');
    }
  }

  function strafeJobActionLabel(status: string): string {
    switch (status) {
      case 'preview': return 'Resume preview';
      case 'queued':
      case 'running': return 'Unschedule all';
      case 'done': return 'Unschedule remaining';
      case 'failed':
      case 'discarded': return 'Discard';
      default: return '';
    }
  }

  async function strafeJobAction(j: StrafeJob) {
    if (j.status === 'preview') {
      await resumeStrafePreview(j.id);
    } else if (j.status === 'failed' || j.status === 'discarded') {
      try {
        await strafeDiscard(j.id);
        strafeJobs = await listStrafes();
      } catch (err: any) {
        showError(err?.message || 'Discard failed');
      }
    } else {
      await unscheduleStrafeJob(j.id);
    }
  }

  async function deleteStrafeFromHistory(j: StrafeJob) {
    if (!confirm(`Delete this Strafe from history?\n\n"${j.strategy.slice(0, 80)}${j.strategy.length > 80 ? '…' : ''}"\n\nThis only removes the history row — already-sent emails are not affected.`)) return;
    try {
      await strafeDiscard(j.id);
      strafeJobs = await listStrafes();
      showToast('Removed from history');
    } catch (err: any) {
      showError(err?.message || 'Delete failed');
    }
  }

  function guessNameFromEmail(email: string): string {
    const local = email.split('@')[0];
    if (!local) return '';
    // firstname.lastname or firstname_lastname
    if (local.includes('.') || local.includes('_')) {
      const sep = local.includes('.') ? '.' : '_';
      return local.split(sep).map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ');
    }
    // single token — just capitalize
    return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
  }

  onMount(async () => {
    // Load only fast data on mount
    try {
      const [internal, personas, docs] = await Promise.all([
        getInternalContacts(),
        getPersonas(),
        getOutreachIndex().catch(() => []),
      ]);
      internalEmails = internal;
      personasData = personas;
      // Default to human persona
      const humanKey = Object.entries(personas.personas).find(([_, p]) => p.label?.includes('(Human)'))?.[0];
      selectedPersona = humanKey || personas.active;
      marketingDocs = docs;
    } catch { /* ignore */ }
    // Prefetch so Successful Outreach opens with rows already there. Fired unawaited:
    // nothing here waits for it, and the tab's own `if (!conversions.length)
    // loadHistory()` finds it done.
    // ⚠️ Each row carries full `outbound_sequence` / `inbound_response` bodies, so this
    // is a heavy eager fetch for a tab many sessions never open. Nothing on this page
    // needs the data — only open-speed pays for it. See DECISIONS 2026-08-24 (tabs/counts).
    loadHistory();
    // Load credits info
    getProspectingCredits().then(c => {
      creditsFree = c.free;
      creditsRemaining = c.remaining; creditsLoaded = true;
      creditsCostPer = c.cost_per ?? creditsCostPer;
    }).catch(() => {});
    // Load contacts list in background (for name resolution)
    getContacts().then(c => { allContacts = c; }).catch(() => {});
  });

  // Parse conversion_tracker's outbound_sequence string into step-by-step
  // emails. Sequence format from _pipeline/conversion_tracker.py:
  //     Email 1 (YYYY-MM-DD, sender@x): <body>
  //     ---
  //     Email 2 (YYYY-MM-DD, sender@x): <body>
  // We split on the "Email N (date, sender):" header so each chunk renders
  // with its own subtitle. Returns [] on no match — caller falls back to raw.
  function parseOutboundSequence(seq: string): { step: number; date: string; sender: string; body: string }[] {
    if (!seq) return [];
    const re = /Email (\d+) \(([^,]+),\s*([^)]+)\):\s*/g;
    const headers: { step: number; date: string; sender: string; idx: number; end: number }[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(seq)) !== null) {
      headers.push({ step: +m[1], date: m[2].trim(), sender: m[3].trim(), idx: m.index, end: m.index + m[0].length });
    }
    if (!headers.length) return [];
    return headers.map((h, i) => {
      const bodyEnd = i + 1 < headers.length ? headers[i + 1].idx : seq.length;
      // Strip the trailing "\n---\n" separator between emails
      const body = seq.slice(h.end, bodyEnd).replace(/\s*\n-+\n\s*$/, '').trim();
      return { step: h.step, date: h.date, sender: h.sender, body };
    });
  }

  let historyLoading = $state(false);
  async function loadHistory() {
    historyLoading = true;
    try {
      const [convs, settings] = await Promise.all([getConversions(), getConversionSettings()]);
      conversions = convs;
      teamMode = settings.team_mode;
    } catch { conversions = []; }
    historyLoading = false;
  }

  // Reactive: reload contacts when client changes
  $effect(() => {
    const client = selectedClient;
    if (client) {
      onClientChange();
    }
  });

  // Auto-populate contact name when email changes
  $effect(() => {
    const email = contactEmail;
    if (email) {
      const match = allContacts.find(c => c.email.toLowerCase() === email.toLowerCase());
      contactName = match?.name || guessNameFromEmail(email);
    } else {
      contactName = '';
    }
  });

  let _skipGeneratedWipe = false;
  // While a Today-Discuss seed is being applied, suppress every auto-reset
  // onClientChange normally does: don't wipe the seeded generatedEmails /
  // draft / edit* (handled today by _skipGeneratedWipe), don't blank
  // contactEmail (so the seeded recipient survives the selectedClient
  // assignment), and don't auto-pick clientEmails[0] when length===1
  // (so we don't race the seed's contactEmail choice). Cleared after the
  // seed effect finishes its setup.
  let _seedActive = false;
  async function onClientChange() {
    // Capture _seedActive at call-time so the post-await auto-pick
    // honors the seed even if the flag flips false before the network
    // call resolves. Without this, a Today-Discuss seed could be
    // clobbered when getClientContacts returned a 1-entry list and the
    // post-await branch ran with _seedActive already cleared.
    const seedingNow = _seedActive;
    // Pin this run to the client that triggered it. The effect below fires on EVERY
    // selectedClient change and does not serialise, so two switches leave two requests
    // in flight; if the first resolves last it wins. That mattered: the late reply
    // overwrote clientEmails and, on a one-contact client, set contactEmail to the OLD
    // client's address while selectedClient already read as the new one — and
    // handleGenerate posts exactly that pair, so outreach for one prospect went out
    // addressed to another. Compare before every post-await assignment.
    const forClient = selectedClient;
    if (!seedingNow) {
      contactEmail = '';
    }
    clientEmails = [];
    if (!_skipGeneratedWipe && !seedingNow) {
      generatedEmails = [];
      draft = null;
    }
    _skipGeneratedWipe = false;
    if (!forClient) return;
    try {
      const emails = await getClientContacts(forClient);
      if (forClient !== selectedClient) return;   // a newer selection already won
      clientEmails = emails;
      if (emails.length === 1 && !seedingNow) contactEmail = emails[0];
    } catch (err) {
      if (forClient !== selectedClient) return;
      console.error('Failed to load contacts:', err);
      clientEmails = [];
    }
  }

  async function handleGenerate() {
    if (!selectedClient || !contactEmail) return;
    generating = true;
    generatedEmails = [];
    draft = null;
    try {
      const seq = await createSequence({
        client: selectedClient,
        contact_email: contactEmail,
        contact_name: contactName || undefined,
        brief: instruction || 'Cold outreach',
        persona: selectedPersona || undefined,
        voice: voiceMode,
        num_emails: numEmails,
        delay_2: delay2Days,
        delay_3: delay3Days,
      });
      // Capture research context
      researchContext = seq.research || '';
      // Populate editable email cards
      generatedVersion++;
      generatedEmails = (seq.emails || []).map((e: any) => ({
        step: e.step,
        subject: e.subject,
        body: e.body,
        recommended_attachments: e.recommended_attachments || [],
        editTo: contactEmail,
        editSubject: e.subject,
        editBody: e.body || '',
        editCcClient: [...editCcClient],
        editCcInternal: [...editCcInternal],
        editCcOther: editCcOther,
        polishing: false,
        saving: false,
        polishChanges: '',
        selectedDocs: (e.recommended_attachments || []).filter((id: string) => marketingDocs.some(d => d.id === id)),
        customFiles: [] as File[],
      }));
      // Also set legacy draft for backward compat
      if (seq.emails?.length) {
        const first = seq.emails[0];
        draft = { to: contactEmail, subject: first.subject, body: first.body, recommended_attachments: first.recommended_attachments || [], research_summary: '' };
      }
    } catch (err: any) {
      showError('Generation failed: ' + (err.message || ''));
    }
    // Refresh credits after generation attempt
    getProspectingCredits().then(c => {
      creditsFree = c.free;
      creditsRemaining = c.remaining; creditsLoaded = true;
      creditsCostPer = c.cost_per ?? creditsCostPer;
    }).catch(() => {});
    generating = false;
  }

  async function handleSendSequence() {
    if (sendingSequence || generatedEmails.length < 2) return;
    if (!confirm('Send email 1 now and schedule the rest?')) return;
    sendingSequence = true;

    const { sendEmail, scheduleEmail } = await import('../lib/api');
    const sequenceId = `${selectedClient}_${Date.now()}`;
    const delays = [0, delay2Days, delay3Days];

    try {
      // Send email 1 immediately
      const email1 = generatedEmails[0];
      const cc1 = [...(email1 as any).editCcClient || [], ...(email1 as any).editCcInternal || []];
      if ((email1 as any).editCcOther?.trim()) cc1.push(...(email1 as any).editCcOther.split(',').map((s: string) => s.trim()).filter(Boolean));

      const fd1 = new FormData();
      fd1.append('to', email1.editTo);
      fd1.append('subject', email1.editSubject);
      fd1.append('body', email1.editBody);
      fd1.append('cc', cc1.join(','));
      fd1.append('thread_id', '');
      fd1.append('client', selectedClient);
      if ((email1 as any).selectedDocs?.length) fd1.append('outreach_doc_ids', JSON.stringify((email1 as any).selectedDocs));
      if ((email1 as any).customFiles?.length) for (const f of (email1 as any).customFiles) fd1.append('files', f);

      const result = await sendEmail(fd1);
      const threadId = result?.thread_id || '';
      showToast('Email 1 sent');

      // Schedule remaining emails
      for (let i = 1; i < generatedEmails.length; i++) {
        const email = generatedEmails[i];
        const cc = [...(email as any).editCcClient || [], ...(email as any).editCcInternal || []];
        if ((email as any).editCcOther?.trim()) cc.push(...(email as any).editCcOther.split(',').map((s: string) => s.trim()).filter(Boolean));

        const sendAt = new Date();
        sendAt.setDate(sendAt.getDate() + (delays[i] || 7));
        sendAt.setHours(9, 0, 0, 0);

        const fd = new FormData();
        fd.append('to', email.editTo);
        fd.append('subject', email.editSubject);
        fd.append('body', email.editBody);
        fd.append('cc', cc.join(','));
        fd.append('thread_id', threadId);
        fd.append('client', selectedClient);
        fd.append('send_at', sendAt.toISOString());
        fd.append('sequence_id', sequenceId);
        fd.append('step', String(email.step));
        if ((email as any).selectedDocs?.length) fd.append('outreach_doc_ids', JSON.stringify((email as any).selectedDocs));
        if ((email as any).customFiles?.length) for (const f of (email as any).customFiles) fd.append('files', f);

        await scheduleEmail(fd);
        showToast(`Email ${email.step} scheduled for +${delays[i]} days`);
      }

      showToast('Sequence launched — auto-cancels if recipient replies');
      generatedEmails = []; draft = null;
    } catch (err: any) {
      showError('Sequence send failed: ' + (err.message || ''));
    }
    sendingSequence = false;
  }

  // Non-success messages render in the red toast. Detected HERE rather than at
  // the ~30 call sites in this file: each already states its outcome in words,
  // and a missed flag fails silently back to green — which is the bug this
  // fixes. Pass `true` explicitly to override.
  const TOAST_FAILURE = /\b(fail|failed|error|invalid|required|can'?t|cannot|empty|denied|unable)\b|^(pick|set|enter|select) /i;
  function showToast(msg: string, isError = TOAST_FAILURE.test(msg)) {
    toast = msg;
    toastError = isError;
    setTimeout(() => toast = '', isError ? 5000 : 4000);
  }

  // Anything reaching a `catch` IS a failure — say so explicitly rather than hoping the
  // wording matches TOAST_FAILURE. The trap is `err?.message || 'X failed'`: the FALLBACK
  // carries the safe word, but when the server does send a message ("Draft not found",
  // "5 emails are already pinned") that fallback never runs, and the toast came out green.
  const showError = (msg: string) => showToast(msg, true);
</script>

{#snippet prospectingChat()}
  <!-- The rail carries no title: the starter below names the assistant, and a heading
       on a panel that is already the only thing in its column is a second label for
       the same object. Matches the client page. -->
  {#if isDealton}
    <div class="strat-devbar">
      ◆ chatMode (dev only)
      <div class="seg">
        {#each [['cro', 'Default'], ['george', $agentNameStore || 'AI Partner'], ['user', 'You']] as [val, label]}
          <button class:on={chatMode === val} onclick={() => chatMode = val as 'cro' | 'george' | 'user'}>{label}</button>
        {/each}
      </div>
    </div>
  {/if}
  <div class="rail-util">
    {#if chatMessages.length > 0}
      <button type="button" class="rail-restart" onclick={() => { chatMessages = []; }}
              title="Clear this conversation and start over">↺ Restart</button>
    {/if}
  </div>
  <div class="chat-messages">
    {#if chatMessages.length === 0 && !chatLoading}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">{$agentNameStore || 'George'} knows what converted before</div>
        <div class="rail-starter-s">Talk through the angle, or pick a target on the right.</div>
      </div>
    {/if}
    {#each chatMessages as msg}
      {#if !msg.hidden}
      {#if msg.role === 'assistant'}
        <!-- An assistant turn is an authored card (`.gmsg`, app.css), not a bubble —
             same as the client page. `.chat-msg` stays for the user's own turns, the
             seed label and the tool-call trail, which this page has and that one
             does not. -->
        <div class="gmsg">
          <div class="gmsg-head">
            <span class="g-avatar">✦</span>
            <span class="g-name">{$agentNameStore || 'George'}</span>
          </div>
          <div class="gmsg-body">
            <div class="md-content">{@html renderSafeMarkdown(msg.content)}</div>
          </div>
        </div>
      {:else}
        <div class="chat-msg {msg.role}"
             class:tool-msg={msg.role === 'system' && !msg.isSeedLabel}
             style={msg.isSeedLabel ? 'align-self:center; background:transparent; border:none; box-shadow:none; max-width:100%; padding:4px 0;' : ''}>
          {#if msg.isSeedLabel}
            <div style="font-size:11px; color:var(--text-dim); font-style:italic; text-align:center;">{msg.content}</div>
          {:else if msg.role === 'system'}
            <div class="tool-calls">{#each msg.content.split('\n') as line}<div>{line}</div>{/each}</div>
          {:else}
            {msg.content}
          {/if}
        </div>
      {/if}
      {/if}
    {/each}
    {#if chatLoading}
      <div class="chat-msg assistant" style="display:flex; align-items:center; gap:8px;">
        <span class="spinner"></span> <span class="text-muted">Thinking...</span>
      </div>
    {/if}
  </div>

  <div class="ask-suggest">
    {#if !chatMessages.some((m) => m.role === 'assistant')}
      <button class="sug-chip primary" onclick={() => quickAsk('Who should I reach out to next?')}>Who should I reach out to next?</button>
      <button class="sug-chip" onclick={() => quickAsk('What angle has been working on accounts like these?')}>What angle is working?</button>
    {:else}
      <button class="sug-chip primary" disabled={chatLoading} onclick={draftFromChat}>
        {chatLoading ? 'Drafting…' : 'Draft email'}
      </button>
    {/if}
    {#if SHOW_FIND_PROSPECTS}
      <button class="sug-chip" disabled={discoveringLookalikes || chatLoading} onclick={findLookalikes}
              title="Use Brave + Claude + email verification to surface fresh lookalike companies based on your top accounts"
        >{discoveringLookalikes ? '🔭 Researching…' : '🔭 Find new prospects'}</button>
    {/if}
  </div>
  <div class="strat-ask">
    <textarea
      bind:value={chatInput}
      placeholder="Discuss prospecting strategy..."
      class="flex-1"
      rows="2"
      style="resize:vertical; min-height:2.4em; max-height:12em; font-family:inherit;"
      onkeydown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
    ></textarea>
    <button class="primary" onclick={sendChat} disabled={chatLoading || !chatInput.trim()}>Send</button>
  </div>
{/snippet}

<div class="page-title">{$terms.nav_prospecting}</div>

<div class="split-shell pr-shell" bind:this={shellEl}
     class:dragging={rail.dragging} class:collapsed={rail.collapsed} style="--rail-w: {rail.w}px">
  <!-- LEFT: the prospecting strategy conversation, beside EVERY tab — it is about
       prospecting as a whole, not about the Outreach form. -->
  <aside class="split-rail">
    {#if !rail.showTab}{@render prospectingChat()}{/if}
  </aside>

  <!-- Splitter: ONE visible control (the grip); the bar itself is the resize target.
       See app.css `.split-divider` and DECISIONS 2026-07-26. -->
  <div class="split-divider" class:collapsed={rail.collapsed}>
    {#if !rail.collapsed}
      <button type="button" class="split-drag"
              aria-label="Resize {$agentNameStore || 'George'} panel — arrow keys adjust"
              onpointerdown={rail.startDrag} onkeydown={rail.onKey}></button>
    {/if}
    <button type="button" class="split-grip" onclick={rail.toggle}
            aria-expanded={!rail.collapsed}
            title="{rail.collapsed ? 'Show' : 'Hide'} {$agentNameStore || 'George'}"
            aria-label="{rail.collapsed ? 'Show' : 'Hide'} {$agentNameStore || 'George'} panel"
      >{rail.collapsed ? '›' : '‹'}</button>
  </div>

  <section class="split-main pr-main">
<div class="tabs">
  {#if rail.showTab}
    <div class="tab tab-assist" class:active={activeTab === 'george'}
      role="button" tabindex="0"
      onclick={() => activeTab = 'george'}
      onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (activeTab = 'george')}>
      <span class="spark">✦</span> {$agentNameStore || 'George'}
    </div>
  {/if}
  <div class="tab" class:active={activeTab === 'generate'}
    role="button" tabindex="0"
    onclick={() => activeTab = 'generate'}
    onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (activeTab = 'generate')}>
    Outreach
  </div>
  <div class="tab" class:active={activeTab === 'history'}
    role="button" tabindex="0"
    onclick={() => { activeTab = 'history'; if (!conversions.length) loadHistory(); }}
    onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { activeTab = 'history'; if (!conversions.length) loadHistory(); } }}>
    Successful Outreach
  </div>
  <div class="tab" class:active={activeTab === 'strafe'}
    role="button" tabindex="0"
    onclick={() => { activeTab = 'strafe'; loadStrafeData(); }}
    onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { activeTab = 'strafe'; loadStrafeData(); } }}>
    Strafe
  </div>
  {#if isDealton}
    <div class="tab" class:active={activeTab === 'meta'}
      role="button" tabindex="0"
      onclick={() => { activeTab = 'meta'; }}
      onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { activeTab = 'meta'; } }}>
      Meta
    </div>
  {/if}
</div>

<div class="pr-mainbody">

{#if activeTab === 'george'}
  <div class="seg-assist">{@render prospectingChat()}</div>
{/if}

{#if activeTab === 'meta' && isDealton}
  <MetaTab />
{/if}

{#if activeTab === 'generate'}
  <div class="card">
    <div class="font-bold mb-3">Outreach Generator</div>
    <div class="text-xs text-dim mb-3">
      Select a prospect, pick a contact, and your partner will generate personalized outreach
      based on your most successful past emails.
    </div>

    <div class="mb-2">
      <label class="text-xs text-dim" for="p-client">Client / Account</label>
      <select id="p-client" bind:value={selectedClient}>
        <option value="">Select a client...</option>
        {#each $clients as c}
          <option value={c}>{c.replace(/_/g, ' ')}</option>
        {/each}
      </select>
    </div>

    <div class="grid-2 mb-2">
      <div>
        <label class="text-xs text-dim" for="p-email">Contact email</label>
        {#if clientEmails.length > 0}
          <select bind:value={contactEmail}>
            <option value="">Select or type below...</option>
            {#each clientEmails as e}
              <option value={e}>{e}</option>
            {/each}
          </select>
        {/if}
        <input id="p-email" bind:value={contactEmail} placeholder="email@company.com" style="margin-top:4px;" />
      </div>
      <div>
        <label for="pr-contactName" class="text-xs text-dim">Contact name</label>
        <input id="pr-contactName" bind:value={contactName} placeholder="Contact name" />
      </div>
    </div>

    <div class="mb-2">
      <label class="text-xs text-dim" for="p-instr">Special instructions (optional)</label>
      <textarea id="p-instr" bind:value={instruction} rows="2"
        placeholder="e.g. Focus on their recent acquisition, mention the gold case study"></textarea>
    </div>

    {#if selectedClient}
      <div class="grid-2 mb-2">
        <MultiSelect label="CC — client contacts" options={clientEmails.filter(e => e !== contactEmail)} bind:selected={editCcClient} />
        <MultiSelect label="CC — your team" options={internalEmails} bind:selected={editCcInternal} />
      </div>
      <div class="mb-3">
        <label for="pr-editCcOther" class="text-xs text-dim">CC — other (comma-separated)</label>
        <input id="pr-editCcOther" bind:value={editCcOther} placeholder="other@company.com" />
      </div>
    {/if}

    {#if personasData}
      <div class="mb-3">
        <label class="text-xs text-dim" for="p-persona">Write as</label>
        <select id="p-persona" bind:value={selectedPersona}
          onchange={() => {
            if (selectedPersona) setActivePersona(selectedPersona);
            // Picking a persona is an explicit act, so it drops any override and lets
            // the derivation speak. The rule that used to live here IS that derivation.
            voiceOverride = null;
          }}>
          {#each Object.entries(personasData.personas) as [key, p]}
            <option value={key}>{p.label}</option>
          {/each}
        </select>
      </div>
    {/if}

    <!-- `align-items: stretch`, not `center`: `.seg` (11px text, 6px padding) and a base
         `button.primary` (13px, 7px) are different button families and cannot be made
         to agree by arithmetic without changing `.seg` for its other four consumers.
         Stretching makes the segmented control take the row's height, so the two match
         by construction. The note beside it opts back out — text, not a control. -->
    <div class="row mb-3" style="gap:12px; align-items:stretch; flex-wrap:wrap;">
      <div class="seg">
        <button class:on={numEmails === 1} onclick={() => numEmails = 1}>1 Email</button>
        <button class:on={numEmails === 3} onclick={() => numEmails = 3}>3-Email Sequence</button>
      </div>
      <button class="primary" onclick={handleGenerate}
        disabled={!selectedClient || !contactEmail || generating}>
        {#if generating}<span class="spinner"></span> Generating...{:else}Generate{/if}
      </button>
      <!-- The allowance states itself HERE, where the credit is spent, rather than as a
           badge on the tab: a tab badge counts what is inside the tab, and `16/20` was
           an allowance, not contents. Past the free 20 nothing is blocked — the backend
           bills `cost_per` (0.20) per generation — so "0 left" would be a lie; that case
           gets its own sentence. Resets on the calendar month: the credit key carries
           `%Y-%m` (`state_db._prospecting_credit_key`). -->
      {#if creditsLoaded}
        <span class="text-xs text-dim" style="align-self:center;">
          {#if creditsRemaining > 0}
            {creditsRemaining} of {creditsFree} free generations left this month
          {:else}
            Free generations used for this month — ${creditsCostPer.toFixed(2)} each from here
          {/if}
        </span>
      {/if}
    </div>
    {#if numEmails === 3}
      <div class="row mb-3" style="gap:12px; align-items:center; font-size:12px; color:var(--text-muted);">
        <span>Timing:</span>
        <label style="display:flex; align-items:center; gap:4px;">
          Email 2 after
          <input type="number" min="1" max="30" bind:value={delay2Days}
            style="width:48px; text-align:center; padding:2px 4px; border:1px solid var(--border); border-radius:var(--radius); font-size:12px;" /> days
        </label>
        <label style="display:flex; align-items:center; gap:4px;">
          Email 3 after
          <input type="number" min="1" max="60" bind:value={delay3Days}
            style="width:48px; text-align:center; padding:2px 4px; border:1px solid var(--border); border-radius:var(--radius); font-size:12px;" /> days
        </label>
      </div>
    {/if}
  </div>

  {#if generatedEmails.length > 0}
    {#each generatedEmails as email, idx (`${generatedVersion}_${idx}`)}
      <!-- Same editor as the client page's composer (ClientView `composeArea`): the
           `.compose-area` surface, a ✦ `.compose-header`, then TO/Subject → CC →
           body → changes → attachments → `.draft-actions`, all on the shared
           draft vocabulary in app.css. The coral left border went with the switch —
           ✦ in the header is George's authorship mark (DESIGN-SYSTEM §5); a coral
           bar down the edge is the status use §1 forbids. -->
      <div class="compose-mb">
      <div class="compose-area mt-4">
        <div class="compose-header"><span class="spark">✦</span> Email {email.step}{generatedEmails.length > 1 ? ` of ${generatedEmails.length}` : ''}</div>

        <div class="grid-2 mb-2">
          <div>
            <label class="text-xs text-dim" for="pr-to-{idx}">TO</label>
            <!-- Contacts picker + free-text input, both bound to the same value —
                 identical to the client composer's TO and to the generator card above.
                 Don't drop the picker: without it you retype an address the CRM
                 already knows. -->
            {#if clientEmails.length > 0}
              <select bind:value={email.editTo}>
                <option value="">Select or type below...</option>
                {#each clientEmails as e}<option value={e}>{e}</option>{/each}
              </select>
            {/if}
            <input id="pr-to-{idx}" bind:value={email.editTo} placeholder="email@company.com" style="margin-top:4px;" />
          </div>
          <div>
            <label class="text-xs text-dim" for="pr-subject-{idx}">Subject</label>
            <input id="pr-subject-{idx}" bind:value={email.editSubject} placeholder="Subject" />
          </div>
        </div>

        <div class="grid-2 mb-2">
          <MultiSelect label="CC — Client contacts" options={clientEmails.filter(e => e !== email.editTo)} bind:selected={email.editCcClient} />
          <MultiSelect label="CC — Your team" options={internalEmails} bind:selected={email.editCcInternal} />
        </div>

        <div class="mb-2">
          <label class="text-xs text-dim" for="pr-ccother-{idx}">CC — Other (comma-separated)</label>
          <input id="pr-ccother-{idx}" bind:value={email.editCcOther} placeholder="other@company.com" />
        </div>

        <!-- No "Body" label — the client composer doesn't label it either; the
             editor is unmistakable and the label only crowded the field. -->
        <div class="mb-3">
          <EmailBodyEditor bind:value={email.editBody} rows={10} placeholder="Email body..." />
        </div>

        <!-- Changes-made panel sits BETWEEN the body and the attachments, as it does
             in the client composer — it reports on the text you just read. -->
        {#if email.polishChanges}
          <div class="polish-changes mb-3">
            <div class="text-xs font-bold mb-1">Changes made:</div>
            <pre class="text-xs" style="white-space:pre-wrap;margin:0;font-family:inherit;">{email.polishChanges}</pre>
          </div>
        {/if}

        <!-- Attachments: the client composer's `attachmentWidget` — labelled
             sections + `.cc-chip` pills + a `.today-mini-btn` "+ Attach file",
             always visible rather than gated on there being something to show.
             Recommended docs carry the coral ✦ (§5, George's authorship) instead
             of the ✨ this page invented. -->
        {#if marketingDocs.length > 0}
          <div class="text-xs text-dim">Attachments</div>
          <div class="chip-row mb-2" style="margin-top:4px;">
            {#each marketingDocs as doc}
              {@const isSelected = email.selectedDocs.includes(doc.id)}
              {@const isRecommended = (email.recommended_attachments || []).includes(doc.id)}
              <span
                class="chip attach" class:selected={isSelected}
                role="button" tabindex="0"
                title={doc.title + (isRecommended ? ' (recommended)' : '')}
                onclick={() => {
                  if (isSelected) {
                    email.selectedDocs = email.selectedDocs.filter((id: string) => id !== doc.id);
                  } else {
                    email.selectedDocs = [...email.selectedDocs, doc.id];
                  }
                }}
                onkeydown={(ev) => {
                  if (ev.key !== 'Enter' && ev.key !== ' ') return;
                  ev.preventDefault();
                  if (isSelected) {
                    email.selectedDocs = email.selectedDocs.filter((id: string) => id !== doc.id);
                  } else {
                    email.selectedDocs = [...email.selectedDocs, doc.id];
                  }
                }}
              >{#if isRecommended}<span class="draft-spark">✦</span>{/if}<svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{doc.title || doc.original_filename}</span>
            {/each}
          </div>
        {/if}
        <div class="text-xs text-dim">Custom files</div>
        <div class="mb-3" style="margin-top:4px; display:flex; flex-wrap:wrap; gap:6px; align-items:center;">
          {#each email.customFiles as f, fi}
            <span class="chip selected" style="display:inline-flex; align-items:center; gap:4px;">
              {f.name}
              <span
                role="button" tabindex="0" style="cursor:pointer; font-size:14px; line-height:1;"
                onclick={() => { email.customFiles = email.customFiles.filter((_: File, i: number) => i !== fi); }}
                onkeydown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && (email.customFiles = email.customFiles.filter((_: File, i: number) => i !== fi))}
              >&times;</span>
            </span>
          {/each}
          <label class="today-mini-btn" style="display:inline-flex; align-items:center; gap:4px; cursor:pointer;">
            + Attach file
            <input type="file" multiple class="sr-only" onchange={(e) => {
              const input = e.target as HTMLInputElement;
              if (input.files) email.customFiles = [...email.customFiles, ...Array.from(input.files)];
              input.value = '';
            }} />
          </label>
        </div>

        <!-- Same order + shared vocabulary as the client composer and Today's draft
             card: Send (coral) · Schedule · secondary pill · separator · ✦ tools
             (borderless). Refine before Polish, per DESIGN-SYSTEM §2's draft
             adjustment tier — this page had them reversed and unlabelled. -->
        <div class="draft-actions">
          {#if generatedEmails.length === 1}
            <button class="btn-primary" disabled={email.saving} onclick={async () => {
              if (!email.editTo) { showToast('Set a To address'); return; }
              if (!confirm('Send this email now?')) return;
              email.saving = true;
              const allCc = [...email.editCcClient, ...email.editCcInternal];
              if (email.editCcOther.trim()) allCc.push(...email.editCcOther.split(',').map((s: string) => s.trim()).filter(Boolean));
              try {
                const { sendEmail } = await import('../lib/api');
                const fd = new FormData();
                fd.append('to', email.editTo); fd.append('subject', email.editSubject);
                fd.append('body', email.editBody); fd.append('cc', allCc.join(','));
                fd.append('thread_id', ''); fd.append('client', selectedClient);
                if (email.selectedDocs.length) fd.append('outreach_doc_ids', JSON.stringify(email.selectedDocs));
                for (const f of email.customFiles) fd.append('files', f);
                await sendEmail(fd);
                showToast('Email sent');
                generatedEmails = []; draft = null;
              } catch (err: any) {
                showError('Send failed: ' + (err.message || ''));
              }
              email.saving = false;
            }}>Send</button>
            <div style="position:relative; display:inline-block;">
              <button class="btn-primary" onclick={() => scheduleOpen = !scheduleOpen}>Schedule</button>
              {#if scheduleOpen}
                <!-- Same popover surface as the client composer: --bg-surface, no
                     hardcoded #fff fallback (which read as a white box in dark mode). -->
                <div style="position:absolute; bottom:100%; left:0; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius); padding:10px; z-index:10; min-width:200px; margin-bottom:4px;">
                  <input type="date" bind:value={scheduleDate} onclick={openPicker} onfocus={openPicker} style="width:100%; margin-bottom:6px; font-size:12px; padding:4px;" />
                  <input type="time" bind:value={scheduleTime} onclick={openPicker} onfocus={openPicker} style="width:100%; margin-bottom:6px; font-size:12px; padding:4px;" />
                  <button class="btn-primary" style="width:100%; font-size:12px;" disabled={email.saving} onclick={async () => {
                    if (!scheduleDate || !scheduleTime) { showToast('Pick a date and time'); return; }
                    if (!email.editTo) { showToast('Set a To address'); return; }
                    email.saving = true;
                    const { scheduleEmail } = await import('../lib/api');
                    const sendAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
                    const allCc = [...email.editCcClient, ...email.editCcInternal];
                    if (email.editCcOther.trim()) allCc.push(...email.editCcOther.split(',').map((s: string) => s.trim()).filter(Boolean));
                    try {
                      const fd = new FormData();
                      fd.append('to', email.editTo); fd.append('subject', email.editSubject);
                      fd.append('body', email.editBody); fd.append('cc', allCc.join(','));
                      fd.append('thread_id', ''); fd.append('client', selectedClient);
                      fd.append('send_at', sendAt);
                      if (email.selectedDocs.length) fd.append('outreach_doc_ids', JSON.stringify(email.selectedDocs));
                      for (const f of email.customFiles) fd.append('files', f);
                      await scheduleEmail(fd);
                      showToast(`Email scheduled for ${scheduleDate} ${scheduleTime}`);
                      scheduleOpen = false;
                      generatedEmails = []; draft = null;
                    } catch (err: any) { showError('Schedule failed: ' + (err.message || '')); }
                    email.saving = false;
                  }}>Schedule Send</button>
                </div>
              {/if}
            </div>
          {/if}
          <button class="today-mini-btn" disabled={email.saving || email.polishing} onclick={async () => {
            email.saving = true;
            const allCc = [...email.editCcClient, ...email.editCcInternal];
            if (email.editCcOther.trim()) allCc.push(...email.editCcOther.split(',').map((s: string) => s.trim()).filter(Boolean));
            try {
              await saveDraft({
                client: selectedClient,
                to: email.editTo,
                subject: email.editSubject,
                body: email.editBody,
                cc: allCc.join(','),
                thread_id: '',
                outreach_doc_ids: email.selectedDocs.length ? email.selectedDocs : undefined,
                files: email.customFiles.length ? email.customFiles : undefined,
              });
              showToast(`Email ${email.step} saved to Drafts`);
            } catch (err: any) {
              showError('Save failed: ' + (err.message || ''));
            }
            email.saving = false;
          }}>{email.saving ? '...' : 'Save draft'}</button>
          <span class="act-sep" aria-hidden="true"></span>
          {#each ['refine', 'polish'] as mode}
            <button class="today-mini-btn draft-ai-btn" disabled={!email.editBody.trim() || email.polishing} onclick={async () => {
              if (!email.editBody.trim() || email.polishing) return;
              email.polishing = true; email.polishChanges = '';
              try {
                const r = await polishEmail({ client: selectedClient, body: email.editBody, subject: email.editSubject, mode: mode as 'polish' | 'refine' });
                email.editBody = r.body; email.editSubject = r.subject; email.polishChanges = r.changes;
              } catch { email.polishChanges = `${mode} failed`; }
              email.polishing = false;
            }}>
              <span class="draft-spark">✦</span> {email.polishing ? '...' : mode === 'polish' ? 'Polish wording' : 'Refine content'}
            </button>
          {/each}
          <!-- Regenerate joins the ✦ tool group only in the single-email case, where
               "regenerate" and "regenerate the sequence" are the same act. For a
               3-email sequence it stays a separate control below the stack, because
               handleGenerate() replaces ALL of them — putting it in one email's bar
               would read as per-email and act globally. -->
          {#if generatedEmails.length === 1}
            <button type="button" class="today-mini-btn draft-ai-btn" disabled={generating} onclick={handleGenerate}>
              <span class="draft-spark">✦</span> {generating ? '...' : 'Regenerate'}
            </button>
          {/if}
        </div>
      </div>
      </div>
    {/each}

    {#if generatedEmails.length > 1}
      <div class="card" style="margin-top: 12px; text-align:center; padding:16px;">
        <div style="font-size:11px; color:var(--text-muted); margin-bottom:8px;">
          Email 1 sends now. Email 2 in +{delay2Days}d. Email 3 in +{delay3Days}d. Auto-cancels if recipient replies.
        </div>
        <!-- Sequence-level commit bar. Same `.draft-actions` vocabulary as a single
             email's bar, just centred — the sequence is one act, so Send/Schedule
             here replace the per-email pair rather than sitting alongside it. -->
        <div class="draft-actions" style="justify-content:center;">
          <button class="btn-primary" disabled={sendingSequence} onclick={handleSendSequence}>
            {#if sendingSequence}<span class="spinner"></span> Sending...{:else}Send sequence{/if}
          </button>
          <div style="position:relative; display:inline-block;">
            <button class="btn-primary" onclick={() => scheduleOpen = !scheduleOpen}>Schedule</button>
            {#if scheduleOpen}
              <div style="position:absolute; bottom:100%; left:0; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius); padding:10px; z-index:10; min-width:220px; margin-bottom:4px;">
                <div style="font-size:11px; color:var(--text-muted); margin-bottom:6px;">Email 1 sends at:</div>
                <input type="date" bind:value={scheduleDate} onclick={openPicker} onfocus={openPicker} style="width:100%; margin-bottom:6px; font-size:12px; padding:4px;" />
                <input type="time" bind:value={scheduleTime} onclick={openPicker} onfocus={openPicker} style="width:100%; margin-bottom:6px; font-size:12px; padding:4px;" />
                <div style="font-size:10px; color:var(--text-muted); margin-bottom:8px;">
                  Email 2: +{delay2Days}d · Email 3: +{delay3Days}d
                </div>
                <button class="primary" style="width:100%; font-size:12px;" disabled={sendingSequence} onclick={async () => {
                  if (!scheduleDate || !scheduleTime) { showToast('Pick a date and time'); return; }
                  sendingSequence = true;
                  const { scheduleEmail } = await import('../lib/api');
                  const sequenceId = `${selectedClient}_${Date.now()}`;
                  const delays = [0, delay2Days, delay3Days];
                  const email1SendAt = new Date(`${scheduleDate}T${scheduleTime}`);
                  try {
                    for (let i = 0; i < generatedEmails.length; i++) {
                      const email = generatedEmails[i];
                      const cc = [...(email as any).editCcClient || [], ...(email as any).editCcInternal || []];
                      if ((email as any).editCcOther?.trim()) cc.push(...(email as any).editCcOther.split(',').map((s: string) => s.trim()).filter(Boolean));
                      const sendAt = new Date(email1SendAt);
                      if (i > 0) { sendAt.setDate(sendAt.getDate() + (delays[i] || 7)); sendAt.setHours(9, 0, 0, 0); }
                      const fd = new FormData();
                      fd.append('to', email.editTo); fd.append('subject', email.editSubject);
                      fd.append('body', email.editBody); fd.append('cc', cc.join(','));
                      fd.append('thread_id', ''); fd.append('client', selectedClient);
                      fd.append('send_at', sendAt.toISOString());
                      fd.append('sequence_id', sequenceId);
                      fd.append('step', String(email.step));
                      if ((email as any).selectedDocs?.length) fd.append('outreach_doc_ids', JSON.stringify((email as any).selectedDocs));
                      if ((email as any).customFiles?.length) for (const f of (email as any).customFiles) fd.append('files', f);
                      await scheduleEmail(fd);
                    }
                    showToast(`Sequence scheduled — email 1 at ${scheduleDate} ${scheduleTime}`);
                    scheduleOpen = false;
                    generatedEmails = []; draft = null;
                  } catch (err: any) { showError('Schedule failed: ' + (err.message || '')); }
                  sendingSequence = false;
                }}>Schedule sequence</button>
              </div>
            {/if}
          </div>
        </div>
      </div>
    {/if}

    <!-- Sequence-only: the single-email case carries ✦ Regenerate in its own action
         bar (see the comment there), so this would be a second control for the same
         act. Same ✦ tool treatment either way. -->
    {#if generatedEmails.length > 1}
    <div class="draft-actions" style="justify-content:center; margin-top:8px;">
      <button type="button" class="today-mini-btn draft-ai-btn" onclick={handleGenerate} disabled={generating}>
        {#if generating}<span class="spinner"></span>{:else}<span class="draft-spark">✦</span> Regenerate all{/if}
      </button>
    </div>
    {/if}

    {#if researchContext && researchContext !== '(No web research results found. Generate email based on available CRM context.)'}
      <div class="card" style="margin-top: 16px;">
        <div class="font-bold mb-2">Web Research</div>
        <div class="md-content text-sm" style="max-height: 300px; overflow-y: auto;">
          {@html renderSafeMarkdown(researchContext)}
        </div>
      </div>
    {/if}
    {/if}

{:else if activeTab === 'history'}
  <!-- Successful Outreach — conversion emails ranked by deal score -->
  {#if historyLoading || (!conversions.length && activeTab === 'history')}
    {#if historyLoading}
      <div class="empty-state"><div class="empty-state-text">Loading...</div></div>
    {:else}
      <div class="empty-state">
        <div class="empty-state-text">No successful outreach tracked yet. Sync your email to detect conversions.</div>
      </div>
    {/if}
  {:else}
    <div class="conv-toolbar">
      <!-- The fact "these 5 are used" is stated EXACTLY once and always: as the divider
           while the rule's order holds, and as this control the moment a different sort
           makes position meaningless. Without it, sorting silently removes the only
           explanation the page has, with no way back. -->
      <!-- Labelled as a SETTING, not a filter chip: it decides where George's
           auto-filled slots come from, so it changes generated output as well as this
           view. Pins are unaffected — a pinned email is used, and shown, whoever owns
           it, which is why "My emails" can still list a teammate's row.
           ⚠️ It governs `autoCount` slots and no more. Pins eat the five from the top, so
           at five pins there is nothing left to scope and the control is a guaranteed
           no-op — it must SAY so rather than accept a click that changes nothing. The
           group header carries the running split; this is the boundary case. -->
      <span class="conv-setting-label">{$agentNameStore || 'George'} learns from</span>
      <div class="seg">
        <!-- Server-side, so flipping it must refetch; setting the flag alone leaves the
             previous rows on screen and the control looks dead. -->
        <button class:on={!teamMode} disabled={autoCount === 0} title={segTitle}
          onclick={async () => {
            if (!teamMode) return;
            const r = await toggleConversionTeamMode();
            teamMode = r.team_mode;
            conversions = await getConversions();
          }}>My emails</button>
        <button class:on={teamMode} disabled={autoCount === 0} title={segTitle}
          onclick={async () => {
            if (teamMode) return;
            const r = await toggleConversionTeamMode();
            teamMode = r.team_mode;
            conversions = await getConversions();
          }}>The team's</button>
      </div>
    </div>


    {@const byRank = (a: ConversionEmail, b: ConversionEmail) => {
      const ka = convKey(a), kb = convKey(b);
      const cmp = ka < kb ? -1 : ka > kb ? 1 : 0;
      return convSortDir === 'asc' ? cmp : -cmp;
    }}
    <!-- Two groups, the same structure the Dashboard uses for stages. Membership is the
         answer to "what does George use", so it must NOT be something a sort can undo:
         sorting reorders WITHIN a group and the top group stays the top group. Pinning
         moves a row across, which is exactly what pinning means. -->
    <!-- "My emails" narrows the VIEW as well as auto-fill, so the toggle is the only
         owner control on the page. Learned rows are kept regardless of owner: pins ignore
         the setting, so filtering them out would leave the group header counting rows it
         does not show. -->
    {@const shown = teamMode ? conversions : conversions.filter(c => c.learned || c.user_email === $userEmail)}
    {@const usedRows = shown.filter(c => c.learned).sort(byRank)}
    {@const restRows = shown.filter(c => !c.learned).sort(byRank)}
    <div class="data-table-wrap">
    <table class="data-table conv-table">
      <thead>
        <tr>
          <!-- Header vocabulary follows the Dashboard table; widths do not, because this
               table's headers are longer. `th` is `white-space: nowrap` at 10px uppercase,
               so each width is floored by its own header text plus the sort arrow — not by
               its content. `Score` matches the Dashboard header and width exactly; the
               `title` is now the only thing distinguishing the CLIENT's deal score from a
               score for the email, so keep it. Inbound is floored by the mono date
               (`2026-02-17`), which `.conv-date-cell` forbids wrapping. These widths are
               HINTS: this table uses auto layout, so the surviving columns reflow to fill the
               container after the `@container` rules drop one. -->
          <th style="width:20px;"></th>
          <th class="center th-sort col-score" style="width:60px;" onclick={() => convSortBy('score')} title="The client's deal score — not a measure of the email">Score<span class="sort-ind" class:active={convSort === 'score'}>{convSortInd('score')}</span></th>
          <th style="width:196px;">Client</th>
          <th>Subject</th>
          <!-- Sorted, not filtered. Narrowing to one person is the learn-from toggle's
               job — a second owner control would let the view disagree with the setting. -->
          <th class="th-sort col-owner" style="width:84px;" onclick={() => convSortBy('owner')}>Owner<span class="sort-ind" class:active={convSort === 'owner'}>{convSortInd('owner')}</span></th>
          <!-- `Inbound` is the Dashboard's word for a message from them, which is what a
               reply is. The gap is its own column: it answers a different question (how
               strong was this example) and does not belong glued to a date. -->
          <th class="th-sort col-inbound" style="width:100px;" onclick={() => convSortBy('replied')}>Inbound<span class="sort-ind" class:active={convSort === 'replied'}>{convSortInd('replied')}</span></th>

        </tr>
      </thead>
      <tbody>
      {#snippet convRow(conv: ConversionEmail)}
        {@const open = expandedId === conv.client}
        <tr class="data-row" class:open
          role="button" tabindex="0"
          onclick={() => expandedId = open ? '' : conv.client}
          onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); expandedId = open ? '' : conv.client; } }}>
          <td class="conv-pin-cell">
            <!-- Pinning MOVES the row above the divider. That jump is the whole feedback
                 loop: forcing an email in is moving it to the top, so the animation and
                 the meaning are the same thing. -->
            <button class="conv-pin" class:on={conv.selected}
              aria-label={conv.selected
                ? `Unpin the ${conv.client.replace(/_/g, ' ')} email to stop ${$agentNameStore || 'George'} using it`
                : `Pin the ${conv.client.replace(/_/g, ' ')} email to be used by ${$agentNameStore || 'George'}`}
              title={conv.selected
                ? `Unpin to stop ${$agentNameStore || 'George'} from using this one`
                : `Pin this email to be used by ${$agentNameStore || 'George'}`}
              onclick={async (e) => {
                e.stopPropagation();
                try {
                  const r = await toggleConversionStar(conv.client);
                  conv.selected = r.starred;
                  conversions = await getConversions();
                } catch (err: any) {
                  showError(err?.message || 'Could not change this');
                }
              }}>📌</button>
          </td>
          <td class="center col-score">
            {#if conv.score}<span class="score-num" style={scoreBadgeStyle(conv.score, $colorScheme)}>{conv.score}</span>{/if}
          </td>
          <td><span class="client-cell-name cell-clamp">{conv.client.replace(/_/g, ' ')}</span></td>
          <td class="conv-subject-cell" title={conv.subject}><span class="cell-clamp">{conv.subject}</span></td>
          <td class="cell-date col-owner">{conv.user_email.split('@')[0]}</td>
          <!-- One column, not Sent + Replied: they are the same day on most rows, so a
               second date column bought nothing. The latency does — a same-day reply is a
               much stronger example than one that took three weeks. -->
          <td class="cell-date conv-date-cell col-inbound">{conv.inbound_date}</td>

        </tr>
        {#if open}
          {@const parsedEmails = parseOutboundSequence(conv.outbound_sequence || conv.body_snippet || '')}
          <tr class="detail-row">
            <td colspan="6">
          <div class="conv-reply-meta">
            Replied {conv.inbound_date} · {replyLatency(conv.outbound_date, conv.inbound_date) || 'no gap recorded'}
            · sent {conv.outbound_date}
          </div>
          <div class="conv-sequence">
            {#if parsedEmails.length}
              {#each parsedEmails as em, eIdx}
                <div class="conv-email" class:conv-email-separator={eIdx > 0}>
                  <div class="conv-email-header">
                    <span class="conv-email-step">Email {em.step}</span>
                    <span class="conv-email-meta">{em.date} · {em.sender}</span>
                  </div>
                  <div class="conv-email-body">{unwrapHardBreaks(repairLostApostrophes(em.body))}</div>
                </div>
              {/each}
            {:else}
              <div class="conv-email-body">{unwrapHardBreaks(repairLostApostrophes(conv.outbound_sequence || conv.body_snippet || ''))}</div>
            {/if}
          </div>
          {#if conv.inbound_response}
            <div class="conv-response">
              <div class="conv-email-header">
                <span class="conv-email-step" style="color: var(--green);">Client response</span>
              </div>
              <div class="conv-email-body">{unwrapHardBreaks(repairLostApostrophes(conv.inbound_response))}</div>
            </div>
          {/if}
          </td>
        </tr>
        {/if}
      {/snippet}

      {#snippet groupHead(label: string, meta: string, open: boolean, toggle: () => void)}
        <tr class="group-row" role="button" tabindex="0"
          onclick={toggle}
          onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle()}>
          <td colspan="6">
            <div class="group-header">
              <span class="group-arrow">{open ? '▼' : '▶'}</span>
              <span class="group-name">{label}</span>
              <span class="group-meta">{meta}{open ? '' : ' · collapsed'}</span>
            </div>
          </td>
        </tr>
      {/snippet}

      <!-- The count is part of the sentence, not a number floating beside it: "learns
           from these 5" is a fact you read, "learns from these · 5" is two to join. -->
      {@render groupHead(
        `${$agentNameStore || 'George'} learns from these ${learnedCount}`, slotSplit,
        usedOpen, () => usedOpen = !usedOpen)}
      {#if usedOpen}
        {#each usedRows as conv (conv.client)}{@render convRow(conv)}{/each}
      {/if}
      <!-- The count lives here, not on the tab: it is a total, and a total on a tab reads
           as "needs you". Here it is the invitation — pinning is how a row joins the group
           above. "N more", not "Others"/"All": the rows above are not in this group. -->
      {#if restRows.length}
        {@render groupHead(
          `${restRows.length} more success case${restRows.length === 1 ? '' : 's'}`,
          `pin one to teach ${$agentNameStore || 'George'}`,
          restOpen, () => restOpen = !restOpen)}
        {#if restOpen}
          {#each restRows as conv (conv.client)}{@render convRow(conv)}{/each}
        {/if}
      {/if}
      </tbody>
    </table>
    </div>
  {/if}
{/if}


{#if activeTab === 'strafe'}
  <div class="card mb-3">
    <!-- The five cards ARE a sequence — you cannot preview without targets and a
         strategy, and steps 3-5 disappear once a preview exists. Numbering them, and
         having each state its own result on the right, is what makes that sequence
         readable; without it the page is one screen of controls with no entry point. -->
    <div class="strafe-card-header">
      <span class="strafe-step-n">1</span>
      <h4>Filters</h4>
      <span class="strafe-step-state">
        {strafeFilteredContacts.length} prospect{strafeFilteredContacts.length === 1 ? '' : 's'} match
      </span>
    </div>

    <div class="strafe-filter-row">
      <div class="strafe-filter-label">Tag</div>
      <div class="chip-row">
        {#each strafeAllTags as t}
          <button class="chip" class:selected={strafeFilterTags.has(t)} onclick={() => strafeToggleTag(t)}>
            {t === '' ? 'Untagged' : t}
          </button>
        {/each}
        {#if strafeAllTags.length === 0}<span class="text-xs text-dim">No tags yet — import contacts with a tag column or bulk-tag.</span>{/if}
      </div>
    </div>

    <div class="strafe-filter-row">
      <div class="strafe-filter-label">Company Stage</div>
      <div class="chip-row">
        {#each strafeAllStages as s}
          <button class="chip" class:selected={strafeFilterStages.has(s)} onclick={() => strafeToggleStage(s)}>{s}</button>
        {/each}
        {#if strafeAllStages.length === 0}<span class="text-xs text-dim">No stages found.</span>{/if}
      </div>
    </div>

    <div class="strafe-filter-row">
      <div class="strafe-filter-label">User Engagement</div>
      <div class="chip-row">
        {#each ['never_contacted','engaged','stale'] as b}
          <button class="chip" class:selected={strafeFilterEngagement.has(b as EngagementBucket)}
            onclick={() => strafeToggleEngagement(b as EngagementBucket)}>
            {ENGAGEMENT_LABELS[b as EngagementBucket]}
          </button>
        {/each}
      </div>
    </div>

  </div>

  <!-- Targets survives the preview: you approve a recipient list, so the list has to be
       on screen at the moment you approve it. Collapsed by default once a preview is up,
       because the samples are what you are reading then — but one click away, and its
       count is always visible. -->
  {#if strafeFilteredContacts.length > 0}
    <div class="card mb-3">
      <div class="strafe-card-header">
        <button type="button" class="strafe-targets-toggle"
                aria-expanded={strafeTargetsOpen}
                onclick={() => strafeTargetsOpen = !strafeTargetsOpen}>
          <span class="strafe-step-n">2</span>
          <h4>Targets</h4>
          <span class="draft-chev" class:open={strafeTargetsOpen}>›</span>
        </button>
        {#if strafeTargetsOpen}
          <span class="text-xs">
            <button class="text-xs" onclick={strafeCheckAll}>Check all</button>
            ·
            <button class="text-xs" onclick={strafeUncheckAll}>Uncheck all</button>
            ·
            <button class="text-xs" onclick={strafeInvertSelection}>Invert</button>
          </span>
        {/if}
        <span class="strafe-step-state">{strafeSelectedEmails.length} of {strafeFilteredContacts.length} checked</span>
      </div>
      <div class="strafe-target-list" class:hidden={!strafeTargetsOpen}>
        {#each [...strafeFilteredContacts].sort((a, b) => {
          const ac = (a.client || '').toLowerCase();
          const bc = (b.client || '').toLowerCase();
          if (!ac && bc) return 1;          // empty client at the end
          if (ac && !bc) return -1;
          if (ac !== bc) return ac.localeCompare(bc);
          return (a.name || a.email).localeCompare(b.name || b.email);
        }) as c}
          <label class="strafe-target-row">
            <input type="checkbox"
              checked={!strafeUncheckedEmails.has(c.email)}
              onchange={() => strafeToggleContact(c.email)} />
            <span class="strafe-target-email" title={c.email}>{c.email}</span>
            <span class="strafe-target-name" title={c.name}>{c.name}</span>
            <span class="strafe-target-client text-dim" title={c.client}>{c.client}</span>
            <span class="text-xs text-dim">{ENGAGEMENT_LABELS[classifyEngagement(c)]}</span>
          </label>
        {/each}
      </div>
    </div>

  {/if}

  {#if strafeFilteredContacts.length > 0 && strafePreviewSamples.length === 0}
    <!-- ── Strategy ── -->
    <div class="card mb-3">
      <div class="strafe-card-header">
        <span class="strafe-step-n">3</span>
        <h4>Strategy</h4>
        <span class="strafe-step-state" class:strafe-step-todo={!strafeStrategy.trim()}>
          {strafeStrategy.trim() ? 'written' : 'required'}
        </span>
      </div>
      <textarea
        bind:value={strafeStrategy}
        placeholder="Describe the angle to write to — e.g. 'Lead with cost savings on Q2 ops, reference recent automation pilots, attach the ROI one-pager.'"
        rows="4"
        style="width:100%;"
      ></textarea>
      <div class="row gap-lg mt-2" style="flex-wrap:wrap; align-items:center;">
        <label class="text-sm">
          Persona:
          <select bind:value={strafePersona}>
            <option value="">— default —</option>
            {#if personasData}
              {#each Object.entries(personasData.personas) as [key, p]}
                <option value={key}>{p.label || key}</option>
              {/each}
            {/if}
          </select>
        </label>
        <label class="text-sm">
          Emails per prospect:
          <select bind:value={strafeNumEmails}>
            <option value={1}>1</option>
            <option value={3}>3</option>
          </select>
        </label>
        {#if strafeNumEmails === 3}
          <label class="text-sm" style="display:inline-flex; align-items:center; gap:6px;" title="Send emails 2 and 3 as replies in the same conversation as email 1, with 'Re:' subject. Recipient sees one threaded conversation instead of three separate emails.">
            <input type="checkbox" bind:checked={strafeThreadChain} style="width:auto;" />
            Thread emails together
          </label>
        {/if}
        <label class="text-sm" style="display:inline-flex; align-items:center; gap:6px;" title="Include past successful outreach examples (emails that got responses) in the prompt as style references. Uncheck to write fresh from product context only.">
          <input type="checkbox" bind:checked={strafeUseConversions} style="width:auto;" />
          Learn from successful outreach
        </label>
      </div>
    </div>

    <!-- ── Schedule (matches Meta tab layout) ── -->
    <div class="card mb-3">
      <!-- The two paragraphs that used to sit under this grid explained `stagger` and the
           0/3/7 default at length — a manual inside a form, and half this card's height.
           Each explanation now hangs off the field it describes, which is the idiom the
           Strategy card above already uses for its two checkboxes, and the settings state
           themselves in the step head so you can read them without expanding anything. -->
      <div class="strafe-card-header">
        <span class="strafe-step-n">4</span>
        <h4>Schedule</h4>
        <span class="strafe-step-state">
          +{Math.max(0, Math.floor(strafeInitialOffsetMinutes))}m{#if strafeNumEmails === 3}{' '}· then +{strafeDelay2Days}d / +{strafeDelay3Days}d{/if} · {Math.max(0, Math.floor(strafeDispatchStagger))}s apart
        </span>
      </div>
      <div class="strafe-schedule-grid">
        <label title="How long after you commit the first email goes out. 0 = as soon as the scheduler next runs.">
          <span>Initial offset (minutes)</span>
          <input type="number" bind:value={strafeInitialOffsetMinutes} min="0" />
        </label>
        {#if strafeNumEmails === 3}
          <label title="Days after email 1. Production default is 3; use 0 to smoke-test the whole sequence at once.">
            <span>Email 2 delay (days)</span>
            <input type="number" bind:value={strafeDelay2Days} min="0" />
          </label>
          <label title="Days after email 1. Production default is 7; use 0 to smoke-test the whole sequence at once.">
            <span>Email 3 delay (days)</span>
            <input type="number" bind:value={strafeDelay3Days} min="0" />
          </label>
        {/if}
        <label title="Gap between one recipient and the next. At 60s, 50 first-emails go out over ~50 minutes instead of in one scheduler tick, which is what keeps Gmail's spam filters calm. 0 sends them back-to-back.">
          <span>Stagger between recipients (seconds)</span>
          <input type="number" bind:value={strafeDispatchStagger} min="0" max="3600" />
        </label>
      </div>
    </div>

    <!-- ── Preview / Launch ── -->
    <div class="card mb-3">
      <div class="strafe-card-header">
        <span class="strafe-step-n">5</span>
        <h4>Preview &amp; send</h4>
      </div>
      <!-- The blast radius, at the size of the decision rather than as 11px small print:
           this is the one action in the app that emails dozens of real prospects. Price
           only appears once it is actually going to be charged. -->
      <div class="strafe-summary mb-2">
        <span class="strafe-summary-main">
          {strafeSelectedEmails.length} prospect{strafeSelectedEmails.length === 1 ? '' : 's'}
          · {strafeNumEmails} email{strafeNumEmails === 1 ? '' : 's'} each
          · <strong>{strafeCost} to send</strong>
        </span>
        <span class="text-xs text-dim">
          Preview costs 3 credits · balance {Math.max(0, creditsRemaining)}{#if strafeOverage > 0}{' '}· this strafe goes {strafeOverage} past it, about ${(strafeOverage * creditsCostPer).toFixed(2)}{/if}
        </span>
      </div>
      <div class="row gap-lg">
        <button class="primary"
          onclick={runStrafePreview}
          disabled={strafePreviewing || strafeSelectedEmails.length < 1 || !strafeStrategy.trim()}>
          {strafePreviewing ? `Generating ${Math.min(3, strafeSelectedEmails.length)} example${Math.min(3, strafeSelectedEmails.length) === 1 ? '' : 's'}...` : `Preview ${Math.min(3, strafeSelectedEmails.length)} example${Math.min(3, strafeSelectedEmails.length) === 1 ? '' : 's'}`}
        </button>
        <!-- Name the missing step, not just "disabled". The button has two blockers and
             the old hint covered one of them, so a page with targets but no strategy
             offered a dead button and no reason. -->
        {#if strafeBlockers.length > 0}
          <span class="text-xs strafe-step-todo" style="align-self:center;">
            {strafeBlockers.join(' · ')}
          </span>
        {/if}
      </div>
    </div>
  {/if}

  {#if strafePreviewSamples.length > 0}
    <div class="card mb-3">
      <div class="strafe-card-header">
        <span class="strafe-step-n">5</span>
        <h4>Review &amp; send</h4>
        <span class="strafe-step-state">{strafePreviewSamples.length} of {strafePreviewTotalPlanned} shown</span>
      </div>
      <!-- The filter chips stay live while this is on screen, and resuming an old job
           restores its settings but not its targets — so the samples can describe a
           different send than the button below fires. Say so where the samples are read. -->
      {#if strafePreviewStale}
        <div class="strafe-stale">
          Previewed for <strong>{strafePreviewedFor.length}</strong> {strafePreviewedFor.length === 1 ? 'prospect' : 'prospects'};
          the current selection is <strong>{strafeSelectedEmails.length}</strong>.
          These samples no longer describe what Strafe would send.
          <button type="button" class="today-mini-btn" onclick={runStrafePreview} disabled={strafePreviewing}>
            {strafePreviewing ? 'Previewing…' : 'Preview again (3 credits)'}
          </button>
        </div>
      {/if}
      <div class="text-xs text-dim mb-3">Each sample below uses your strategy verbatim. If they look good, the remaining {strafePreviewTotalPlanned - strafePreviewSamples.length} will be generated in the background.</div>

      {#each strafePreviewSamples as s (s.contact_email)}
        <div class="strafe-sample">
          <button class="strafe-sample-header" onclick={() => strafePreviewToggle(s.contact_email)}>
            <span class="strafe-sample-arrow">{strafePreviewExpanded.has(s.contact_email) ? '▼' : '▶'}</span>
            <span class="font-bold">{s.contact_email}</span>
            {#if s.contact_name}<span class="text-dim">— {s.contact_name}</span>{/if}
            <span class="text-xs text-dim">({s.client || 'no client'} · {ENGAGEMENT_LABELS[s.engagement]})</span>
          </button>
          {#if strafePreviewExpanded.has(s.contact_email)}
            <div class="strafe-sample-body">
              {#each s.emails as e}
                <div class="strafe-step">
                  <div class="text-xs text-dim">Step {e.step} · +{e.delay_days}d</div>
                  <div class="font-bold text-sm">Subject: {e.subject}</div>
                  <div class="strafe-step-body">{@html renderSafeMarkdown(e.body)}</div>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/each}

      <div class="row gap-lg mt-3">
        <button class="primary"
          onclick={commitStrafe}
          disabled={strafeCommitting}>
          {strafeCommitting ? 'Committing...' : `Strafe all ${strafeSelectedEmails.length}`}
        </button>
        <button onclick={() => { strafePreviewSamples = []; strafePreviewJobId = ''; }}
          disabled={strafeCommitting}>
          Tweak strategy
        </button>
        <button onclick={discardStrafe} disabled={strafeCommitting}>Discard preview</button>
      </div>
    </div>
  {/if}

  {#if strafeJobs.length > 0}
    <div class="card mb-3">
      <div class="font-bold mb-2">Recent strafes</div>
      {#each strafeJobs as j (j.id)}
        <div class="strafe-job-row">
          <div class="row gap-lg" style="align-items:center; flex-wrap:wrap;">
            <span class="text-xs text-dim">{j.created_at.split('.')[0].replace('T', ' ')}</span>
            <span class="text-xs">{j.total} prospect{j.total === 1 ? '' : 's'}</span>
            <span class={`strafe-job-status status-${j.status}`}>{j.status}</span>
            {#if j.status === 'preview'}
              <button type="button" class="strafe-job-resume" onclick={() => resumeStrafePreview(j.id)}>
                Resume preview
              </button>
            {/if}
          </div>
          <div class="strafe-job-strategy" title={j.strategy}>{j.strategy}</div>
          {#if j.status !== 'preview'}
            <div class="strafe-progress">
              <div class="progress-bar"><div class="progress-fill" style="width:{j.total ? (j.completed / j.total) * 100 : 0}%;"></div></div>
              <div class="text-xs text-dim">
                {j.completed}/{j.total} scheduled{j.failed ? ` · ${j.failed} failed` : ''}
              </div>
            </div>
          {/if}
          <div class="row gap-lg" style="margin-top:6px;">
            <button class="text-xs" onclick={(e) => { e.stopPropagation(); strafeJobAction(j); }}>
              {strafeJobActionLabel(j.status)}
            </button>
          </div>
          {#if j.status === 'preview' || j.status === 'done' || j.status === 'failed' || j.status === 'discarded'}
            <button class="strafe-job-trash"
              title="Remove this Strafe from history (does not affect sent emails)"
              onclick={(e) => { e.stopPropagation(); deleteStrafeFromHistory(j); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
            </button>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
{/if}

    </div>
  </section>
</div>

{#if toast}
  <div class="toast" class:toast-success={!toastError} class:toast-error={toastError}>{toast}</div>
{/if}

<style>
  /* ── Split workspace shell (app.css `.split-shell`) ──
     This page's chrome above the shell: 36px `.main-content` pad-top + the page
     title (24px × 1.6 + its 6px margin) = 80.4px, rounded UP to 84. Round UP
     deliberately: too small a chrome makes the rail taller than the space it has
     and opens a second page scrollbar; too large only leaves a few px unused.
     The tab bar is NOT in this number — it sits inside `.split-main`. The shared
     rule sets no default, so re-measure here if the title block changes.
     There is no page subtitle: the sentence that was here described the Outreach
     tab, not the page, and the Outreach Generator card already says it at length. */
  .pr-shell { --split-chrome: 84px; }

  /* Rail children either side of the log must not be squeezed by it —
     `.chat-messages` is `flex: 1` and everything else has to hold its height. */

  /* The tab bar pins to the top of the page scroller. Works ONLY because nothing
     between here and `.main` is a scroll container or clips — `.split-shell` and
     `.split-main` deliberately set no overflow. z-index 5 sits below MultiSelect's
     dropdown (50) and the Schedule popover (10), so both still open over the bar. */
  .pr-main .tabs {
    position: sticky;
    top: 0;
    z-index: 5;
    background: var(--bg);
    margin-bottom: 0;
  }
  /* No bottom padding — `.main` already closes the page with 28px. */
  .pr-mainbody { padding: 18px 0 0; }

  /* The draft editor carries NO styles of its own — `.compose-area` + its field
     rules, `.compose-mb`, `.act-sep`, `.polish-changes`, `.spark`, `.cc-chip`,
     `.draft-actions`, `.today-mini-btn`, `.draft-ai-btn` and `.draft-spark` are all
     global in app.css, shared with the client page's composer. The two editors stay
     separate components (their prompt logic differs); only the vocabulary is shared.
     Add composer styling THERE, not here, or the two pages drift again. */

  /* Expanded conversion view: per-email blocks with clear separation */
  .conv-sequence {
    margin-bottom: 8px;
    background: var(--bg-hover);
    color: var(--text);
    border-radius: 6px;
    overflow: hidden;
  }
  .conv-email {
    padding: 10px 12px;
    font-size: 13px;
  }
  .conv-email-separator {
    border-top: 1px solid var(--border);
  }
  .conv-email-header {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 6px;
  }
  .conv-email-step {
    font-weight: 700;
    font-size: 12px;
    color: var(--accent-ink);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .conv-email-meta {
    font-size: 11px;
    color: var(--text-dim);
  }
  /* `pre-wrap` honours the newlines in the stored body, so the sender's ~76-col
     plaintext wrap must be undone BEFORE it gets here — `unwrapHardBreaks()` at
     every render site — or the text stops dead at half the container width.
     Same fix ClientView's Emails tab and Today's thread bodies already carry. */
  /* 🔴 `overflow-wrap: anywhere` is LOAD-BEARING for the whole table, not cosmetic. This body
     sits in the expanded row's `<td colspan="6">`, so its min-content is the TABLE's width floor
     — the same mechanism `.cell-clamp` above exists to defeat. `pre-wrap` wraps at spaces only,
     and email bodies are full of unbroken tokens: measured 2026-09-06 in the sandbox,
     **56 of 140 rows carry a token over 60 chars, the longest a 407-character SendGrid tracking
     URL** (~2400px). Without this, expanding one of those rows re-introduces the sideways scroll
     for the entire table, and only for rows a reader happens to open.
     ⚠️ `break-word` does NOT work here — it breaks the rendered line but leaves intrinsic
     min-content at the longest token, which is exactly the value that sets the floor. */
  .conv-email-body {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font-size: 13px;
    line-height: 1.5;
    color: var(--text);
  }
  .conv-response {
    background: var(--bg-hover);
    color: var(--text);
    padding: 10px 12px;
    border-radius: 6px;
    border-left: 3px solid var(--green);
  }

  /* Progressive column removal in priority order, on the TABLE'S CONTAINER. This table lives
     inside `.split-main`, so it is 480px wide beside a wide rail on a large screen and a
     viewport `@media` rule would never fire for it.
     Order, and why: this table answers "which emails does George learn from", so pin · Client ·
     Subject are the answer and never leave. Inbound goes first (the date the reply landed —
     interesting, not load-bearing), then Owner (the learn-from toggle already scopes by
     person), then Score last, since it is what the default sort is on.
     Each rule takes the `<th>` and its `<td>`s together through the shared `col-*` class. */
  /* Shrink first, drop second. Dashboard tightens padding and type at its breakpoints
     (`@media (max-width: 720px)` there); this table never did, so its only adaptation was
     losing a column. 700px sits just above the first drop, so the table gives ground twice
     before anything disappears: cells narrow continuously (`.cell-clamp`), then the row
     tightens, then Inbound goes.
     Padding only touches the horizontal axis — `--row-h` (app.css) owns the height, so the
     rows do not change size. */
  @container data-table (max-width: 700px) {
    .conv-table th, .conv-table td { padding-left: 10px; padding-right: 10px; }
    .conv-table td { font-size: 12px; }
  }
  @container data-table (max-width: 660px) {
    .conv-table .col-inbound { display: none; }
  }
  @container data-table (max-width: 560px) {
    .conv-table .col-owner { display: none; }
  }
  @container data-table (max-width: 460px) {
    .conv-table .col-score { display: none; }
  }

  /* 🔴 NO `table-layout: fixed` on `.conv-table` — and do not add it back.
     Under it the width-less `Subject` column is given its HEADER'S CONTENT width instead of
     the table's remaining space, so the columns stop summing to the table width: the
     remainder renders as trailing white space and the `nowrap` headers print over each other
     (`SUBJECT` over `OWNER`). Measured on the sibling `.contacts-table`: element 480px,
     columns summing to 354 — the fixed columns plus exactly the rendered width of the word
     "CONTACT". Neither `min-width` nor `width: max(100%, …)` helps; both resize the element
     without redistributing the columns.
     Auto layout lets the surviving columns reflow to fill the container, which is the
     adaptation strategy — see `.data-table-wrap` in `app.css`. Dashboard has always used auto
     layout and has never had this bug. */


  /* The pin is quiet until you need it: invisible at rest, visible on row hover, and
     permanently visible once ON — a pinned row must not look unpinned. */
  .conv-setting-label { font-size: 11.5px; color: var(--text-dim); }
  .conv-pin-cell { text-align: center; }
  .conv-pin-head { white-space: nowrap; cursor: help; }
  .conv-pin-head-mark { filter: grayscale(1); opacity: 0.5; font-size: 10px; }
  .conv-pin-info {
    display: inline-block;
    margin-left: 3px;
    font-size: 9px;
    color: var(--text-dim);
    border: 1px solid var(--border-strong);
    border-radius: 50%;
    width: 12px;
    height: 12px;
    line-height: 11px;
    text-align: center;
  }
  .conv-reply-meta { font-size: 11.5px; color: var(--text-dim); margin-bottom: 8px; }
  .conv-pin {
    background: none;
    border: none;
    padding: 2px;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    opacity: 0;
    filter: grayscale(1);
    transition: opacity 0.1s, filter 0.1s;
  }
  .data-row:hover .conv-pin { opacity: 0.55; }
  .conv-pin:focus-visible { opacity: 1; }
  .conv-pin.on, .data-row:hover .conv-pin.on { opacity: 1; filter: none; }

  /* 🔴 A `<td>` does NOT ellipsise under auto table layout, and this rule used to
     assume it did (`overflow: hidden; text-overflow: ellipsis; white-space: nowrap` on the
     two cells). `nowrap` makes the column's MIN-CONTENT the whole string, so the column
     simply grows to fit, the text never overflows, and the ellipsis never fires. Nothing
     bounds a table cell's width, so there is nothing for `text-overflow` to act on.
     Measured 2026-09-06 against `conversion_emails`: subjects reach 111 chars (~650px at
     13px) against a `.split-main` that floors at 480px — so the table's own width floor was
     ~1100px and `.data-table-wrap`'s last-resort `overflow-x` was the everyday behaviour.
     No `@container` breakpoint could reach it: even down to pin · Client · Subject the floor
     was still ~880px.
     Clamp an inner BLOCK instead. Wrapping text has a min-content of its longest word, and
     `overflow-wrap: anywhere` takes that to a single character — which is what actually lets
     the column give ground, so the survivors shrink before any column is dropped.
     ⚠️ `display: -webkit-box` belongs on the SPAN, never on the `<td>`: a `<td>` that stops
     being a table-cell leaves the row's layout and the separators stop lining up. */
  .cell-clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    overflow: hidden;
    /* `anywhere`, NOT `break-word` — only `anywhere` lowers the intrinsic min-content size,
       which is the entire point here. `break-word` breaks the rendered line and leaves the
       column's floor at the longest word. */
    overflow-wrap: anywhere;
  }
  .conv-subject-cell { color: var(--text-muted); }
  .conv-date-cell { white-space: nowrap; }

  .conv-toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-bottom: 10px;
  }
  .conv-latency { color: var(--text-dim); margin-left: 6px; }

  /* ═══ Successful Outreach rows — Today's card anatomy, one size down ═══
     Sizes are deliberately a step under Today's `.draft-card` (15px/18px padding,
     15px title): this is a reference list you scan, not a work queue you act on.
     `.draft-chev`, `.draft-actions.inline-right`, `.today-mini-btn` and
     `.status-pill` all come from app.css — only the row's own geometry is here. */
  .conv-card {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 14px;
    margin-bottom: 6px;
  }
  /* Not-in-use rows recede. Opacity, not hue — the row already says IN USE in
     words, and §1 keeps green for on-track deal status. Do NOT reintroduce a coloured
     left bar for this — a green/blue one breaks that rule twice over. */
  .conv-card.dim { opacity: 0.65; }
  /* The head is the expand control: a real button stripped back to a text block,
     so the whole row stays clickable without a role="button" wrapper. */
  .conv-head {
    /* MUST be block: the global `button` rule is `display: inline-flex`, which lays the
       three lines of the head out side by side instead of stacking them. */
    display: block;
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }
  /* Star: a ghost mini-button like Today's quick actions, coral only once ON —
     that is George's/brand action colour marking a deliberate pick, not a status. */
  .conv-star { font-size: 14px; line-height: 1; padding: 3px 7px; }
  .conv-star.starred { color: var(--accent-ink); }
  /* Neutral by §6 — magnitude is never hue-coded; the number carries it. */
  .score-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 20px;
    padding: 0 5px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
    background: var(--bg-hover);
    color: var(--text);
  }

  .tool-msg {
    align-self: center !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    max-width: 100% !important;
    padding: 4px 0 !important;
  }
  .tool-calls {
    font-size: 11px;
    color: var(--text-dim);
    font-style: italic;
  }
  .tool-calls div {
    padding: 1px 0;
  }
  /* ── Strafe ────────────────────────────────────────────────────── */
  .strafe-filter-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 4px 0;
  }
  .strafe-filter-label {
    min-width: 90px;
    font-weight: 600;
    font-size: 13px;
    padding-top: 4px;
  }
  /* Chevron + label read as one control, so the whole thing is the button. */
  .strafe-targets-toggle {
    display: inline-flex; align-items: center; gap: 12px;
    background: none; border: none; padding: 0; cursor: pointer; color: var(--text);
  }
  .strafe-targets-toggle h4 { margin: 0; font-size: 14px; font-weight: 600; }
  .strafe-summary { display: flex; flex-direction: column; gap: 2px; }
  .strafe-summary-main { font-size: 13px; }
  /* Warning, not error: the send is still allowed — the operator may have trimmed a
     name on purpose. `--orange` is the system's needs-you hue (DESIGN-SYSTEM §amber —
     the token is `--orange`, there is no `--amber`); red would read as blocked. */
  .strafe-stale {
    display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
    font-size: 12px; margin-bottom: 12px; padding: 8px 11px;
    border-radius: var(--radius);
    color: var(--text);
    background: color-mix(in srgb, var(--orange) 12%, var(--bg-surface));
    border: 1px solid color-mix(in srgb, var(--orange) 35%, var(--border));
  }
  .strafe-target-list {
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 4px 0;
  }
  .strafe-target-row {
    display: grid;
    grid-template-columns: 24px 1fr 1fr 1fr auto;
    gap: 8px;
    align-items: center;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
  }
  .strafe-target-row:hover {
    background: var(--bg-hover);
  }
  /* 🔴 `min-width: 0` is load-bearing. A grid item defaults to `min-width: auto`, so it
     refuses to shrink below its content — and inside `.split-main` these three 1fr tracks
     are ~120px each next to a wide rail. A monospace address is ~230px, so without this it
     overflows into the neighbouring column. Ellipsis rather than wrap: these rows are a
     scannable list, and the full value is in the `title`. */
  .strafe-target-email,
  .strafe-target-name,
  .strafe-target-client {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .strafe-target-email {
    font-family: monospace;
  }
  .strafe-sample {
    border: 1px solid var(--border);
    border-radius: var(--radius);
    margin-bottom: 8px;
  }
  .strafe-sample-header {
    width: 100%;
    text-align: left;
    background: var(--bg-hover);
    border: none;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .strafe-sample-arrow {
    width: 14px;
  }
  .strafe-sample-body {
    padding: 8px 14px 12px;
  }
  .strafe-step {
    margin-top: 8px;
    border-top: 1px dashed var(--border);
    padding-top: 8px;
  }
  .strafe-step-body {
    font-family: inherit;
    font-size: 13px;
    margin: 4px 0 0 0;
    line-height: 1.45;
  }
  .strafe-step-body :global(p) { margin: 0 0 6px 0; }
  .strafe-step-body :global(ul),
  .strafe-step-body :global(ol) { margin: 4px 0 6px 20px; }
  .strafe-step-body :global(strong) { font-weight: 700; }
  .strafe-card-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
  .strafe-card-header h4 { margin: 0; font-size: 14px; font-weight: 600; }
  /* The step number carries the sequence; it is not a status, so it stays neutral —
     a coral or green numeral here would encode progress, which §1 keeps off counts. */
  .strafe-step-n {
    display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 20px; height: 20px; border-radius: 50%;
    background: var(--bg-hover); color: var(--text-muted);
    font-size: 11px; font-weight: 700; font-family: var(--font-mono);
  }
  /* Each step reports its own result on the right edge, so the sequence can be read
     without opening anything. `margin-left: auto` is what pins it there. */
  .strafe-step-state { margin-left: auto; font-size: 12px; color: var(--text-muted); }
  /* Outstanding, not broken — `--orange` is the system's needs-you hue. */
  .strafe-step-todo { color: var(--orange-ink); }
  .strafe-schedule-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .strafe-schedule-grid label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; }
  .strafe-schedule-grid input { padding: 6px 8px; border: 1px solid var(--border); border-radius: 4px; font-size: 13px; }
  .strafe-job-row {
    position: relative;
    border-top: 1px solid var(--border);
    padding: 10px 4px;
  }
  .strafe-job-trash {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 22px;
    height: 22px;
    padding: 3px;
    background: transparent;
    border: none;
    color: var(--text-muted);
    opacity: 0.45;
    cursor: pointer;
    border-radius: 3px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .strafe-job-trash:hover {
    opacity: 1;
    background: color-mix(in srgb, var(--text-muted) 12%, transparent);
  }
  .strafe-job-row:first-child { border-top: none; }
  /* Resuming is the row's one navigational action, so it is a real button rather than a
     click handler on the row — the row also holds the job-action and trash buttons, and
     an interactive row wrapping interactive children is neither valid nor reachable by
     keyboard in any predictable order. */
  .strafe-job-resume {
    background: none; border: none; padding: 0;
    color: var(--accent-ink); font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }
  .strafe-job-resume:hover { text-decoration: underline; }
  .strafe-job-strategy {
    font-style: italic;
    margin-top: 4px;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
  }
  .hidden { display: none; }
  .strafe-job-status {
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.05em;
    padding: 2px 6px;
    border-radius: 3px;
    background: var(--bg-hover);
  }
  /* Tokens, not hex. `#3b82f6` is not `--blue` (`#2563eb` light / `#60a5fa` dark), so the
     hardcoded set rendered identically in all three themes — wrong in two of them. Same
     hex, same mistake, as the credit badge in DECISIONS 2026-08-14. The `-ink` variants
     are the readable-on-tint pair; `color-mix` keeps the tint tracking the theme. */
  .strafe-job-status.status-running {
    background: color-mix(in srgb, var(--blue) 15%, transparent); color: var(--blue);
  }
  .strafe-job-status.status-done {
    background: color-mix(in srgb, var(--green) 15%, transparent); color: var(--green-ink);
  }
  .strafe-job-status.status-failed {
    background: color-mix(in srgb, var(--red) 15%, transparent); color: var(--red-ink);
  }
  .strafe-progress { margin: 6px 0; }
  .progress-bar {
    height: 6px;
    background: var(--bg-hover);
    border-radius: 3px;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: var(--blue);
    transition: width 0.3s;
  }
</style>

